/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, EventEmitter, OnDestroy, OnInit, ViewChild } from "@angular/core";

import { Subject, Subscription } from "rxjs";

import * as createjs from 'createjs-module';

import { EmGroup } from "../../models/em-group";
import { Group } from "../../models/group";
import { LocationInstance } from "../../models/location-instance";
import { ObjectInstance } from "../../models/object-instance";
import { ZoneInstance } from "../../models/zone-instance";
import { MainDisplayParameter } from "./main-display-parameter";
import { LocationService } from "../../services/location.service";
import { EntryExitWebSocketService } from "../../services/entry-exit-web-socket.service";
import { ObjectService } from "../../services/object.service";
import { MapComponent, MappingEvent } from "../map/map.component";
import { EventInstance } from "../../models/event-instance";
import {
  EVENT_NAME_AssetEnteredLocation,
  EVENT_NAME_AssetEnteredZone,
  EVENT_NAME_AssetExitedLocation,
  EVENT_NAME_AssetExitedZone,
  EVENT_NAME_PersonEnteredLocation,
  EVENT_NAME_PersonEnteredZone,
  EVENT_NAME_PersonExitedLocation,
  EVENT_NAME_PersonExitedZone,
  EVENT_PROP_Asset,
  EVENT_PROP_Person,
  EVENT_TYPE_Asset,
  EVENT_TYPE_Person
} from "../../app-constants";
import { ImageService } from "app/services/image.service";
import { CookieService } from "ngx-cookie-service";
import { LocateFilters } from "../../models/locateFilters";
import { LocateFilter } from "../../models/locateFilter";
import { ActivatedRoute } from "@angular/router";
import { PropertyType } from "../../models/property-type";
import { PropertyDescription } from "../../models/propertyDescription";
import { FilterPopupDialogComponent } from "./filter-popup-dialog.component";
import { TabType } from "./tabType";
import { Comparison } from "./comparison";
import { DisplayModule } from "app/shared/display.module";
import { ObjectProperty } from "app/models/object-property";
import { InstanceFunctionDialogComponent } from "./instance-function-dialog/instance-function-dialog.component";
import { InstanceDataDialogComponent } from "./instance-data-dialog/instance-data-dialog.component";
import { EventData } from "app/models/event-data";
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from "ngx-spinner";
import { ObjectCategory } from "app/models/object-category";

declare var $: any;

export const Locate_ResultsPerPage = 50;
/**
 * Locate Page of the Edgefinity Application.
 *
 * The locate page shows the location of assets and people in the system. There are two modes - Location mode and Select mode.
 *
 * In Location mode, the user selects a location and all assets and people in that location are shown.
 * In Select mode, the user selects any assets and/or people in the system and those are all shown in the locate page. If the
 * assets and/or people are in different locations, tabs appear above the location map to allow changing of locations to view
 * the other assets and/or people selected.
 *
 * Three different filters are provided - location filters, asset filters and people filters. In location mode, the
 * asset and people filters are also filtered by the location.
 *
 * The locate page defaults to select mode. Selecting a location changes the locate page to Location mode. Deselecting a
 * location changes the locate page back to select mode.
 *
 */

@Component({
  templateUrl: './locate.component.html',
  styleUrls: ['./locate.component.css']
})
export class LocateComponent implements OnInit, OnDestroy {
  private cookieValue: string;

  /** MapComponent for viewing the location chosen inside the Locate Component */
  @ViewChild('locateMap')
  locateMap: MapComponent;

  /** FilterDialog component */
  @ViewChild('filterDialog')
  filterDialog: FilterPopupDialogComponent;

  /** Reference to the AssetFunctionDialog used to execute Function Action(s) on Assets */
  @ViewChild(InstanceFunctionDialogComponent) assetFunctionDialogComponent: InstanceFunctionDialogComponent;

  /** Reference to the AssetDataDialog used to execute the "Edit Data" Action on Assets */
  @ViewChild(InstanceDataDialogComponent) assetDataDialogComponent: InstanceDataDialogComponent;

  /** There are two modes the locate page can be in. Location Mode or Select Mode. This flag determines which we are in */
  isLocationMode = false;

  /** set if the the location currently loading (image and data have not returned from server) */
  isLocationLoading = false;

  //Tab Variables
  mTabTypes = TabType;  //Reference to the Tab Type Enum so that it can be used in the template
  mSelectedTabType = TabType.LOCATIONS;  //Holds the currently selected Tab (People/Assets/Vehicles)

  //Location Variables

  /** Holds all the location information retrieved by the REST API. Note that LocationUIs are hierarchical - unlike People or Assets.
   * Thus, mLocationUis[-1] is all the top-level locations,
   * while a mLocations[x] are all the children locations under Location x */
  mLocationUis: { [key: number]: LocationUi[] } = [];

  mDisplayedLocationUis: LocationUi[] = []; //Holds the Location information that is being displayed in the LHS (this is with filters applied)

  //People Variables
  mPeopleUis: PeopleUI[] = [];  //Holds all the User information retrieved by the REST API
  mDisplayedPeopleUis: PeopleUI[] = []; //Holds the User information that is being displayed in the LHS (this is with filters applied)
  mSelectedPeople: { [key: number]: PeopleUI } = {}; //Holds the Users that are currently have opened details (if any)
  mSelectedPeopleGroup: Group = null; //Holds the User group that is currently selected/being displayed/tracked (if any)
  mPeopleDisplayedForSelectedPeopleGroup: PeopleUI[] = null; //Holds the Users who are being displayed/tracked thanks to their group being selected
  mCheckedPeople: PeopleUI[] = []; //KHT 08/03/2021 - Holds the People who are curently checked

  //Asset Variables
  mAssetUis: AssetUI[] = [];  //Holds all the Asset information retrieved by the REST API
  mDisplayedAssetUis: AssetUI[] = []; //Holds the Asset information that is being displayed in the LHS (this is with filters applied)
  mSelectedAssets: { [key: number]: AssetUI } = {}; //Holds the Asset that is currently selected/being displayed/tracked (if any)
  mSelectedAssetGroup: EmGroup = null;  //Holds the Asset Group that is currently selected/being displayed/tracked (if any)
  mAssetsDisplayedForSelectedAssetGroup: AssetUI[] = null;  //Holds the Assets who are being displayed/tracked thanks to their group being selected
  mCheckedAssets: AssetUI[] = []; //KHT 08/03/2021 - Holds the Assets who are curently checked

  //Location tab being displayed
  mSelectedLocation: LocationInstance = null;
  // Locations shown as tabs
  shownLocations: ShownLocation[] = [];

  //Zones being displayed
  mSelectedZone: ZoneInstance = null;
  mDisplayZones = true;
  mDisplayZoneNames = true;
  mZoneUis: ZoneUi[] = [];

  displayPerimeters = false;

  //Variable holding the available Search Types (NAME/GROUP). This may need to get seperated out for the different types (People/Asset/Vehicles) in the future.
  mSearchTypes;

  // Locations Search Variables
  mLocationsSearchValue = '';

  //People Search Variables
  mSelectedPeopleSearchType = SearchType.NAME;
  mPeopleSearchValue = '';
  allPeopleSelected = false;
  allPeopleSameLocation = false;

  //Asset Search Variables
  mSelectedAssetSearchOption: AssetSearchOption;
  mAssetSearchOptions: AssetSearchOption[];
  mSelectedAssetSearchType = SearchType.NAME;
  mAssetSearchValue = '';
  allAssetsSelected = false;
  allAssetsSameLocation = false;

  //Ruler Variables
  mRulerWidthInPixels = 0;
  mRuleWidthString = this.mRulerWidthInPixels + 'px';
  mRuleWidthDisplay = '';

  //Drawing Variables
  mStage: createjs.Stage = null;  //Stage where everything is drawn
  mBlueprintBitmap: createjs.Bitmap = null; //Location Image
  mScaleMultiplier = 20;  //Location Scale Modifier (Scaling being done by allowing this value to range from 1 - 100, where 20 = 1.  So 1 = .05 which is 5%.  5% => 500%)
  mCanvasWidth: number;   //Width of the Canvas where everything is being drawn
  mCanvasHeight: number;  //Height of the Canvas where everything is being drawn
  mCanvasPageX: number;   //Page X offset for the Canvas, used when determining where to display the popups
  mCanvasPageY: number;   //Page Y offset for the Canvas, used when determining where to display the popups

  //Function that is listening to the createjs.Ticker events so that the Stage can be updated.  Need to keep a reference to remove the function from the tick events.
  mOnTickFunction: Function;

  //Timer used on Window Resize.  Delays the resizing of the Canvas until the User has stopped resizing the Window for half a second.  Done to improve performance/limit the number of times the Canvas is resized.
  mCanvasResizeTimer = null;

  //Variables related to monitoring/tracking Assets/People on the left-side pane
  mEnterExitWsSubject: Subject<MessageEvent>;
  mEnterExitWsSubscription: Subscription;

  objectReferenceName: { [key: number]: String } = [];

  NUM_LOCATION_TABS_SHOWN = 5;

  // Used for filters
  locateFilters: LocateFilters; // all filters
  currentFilters: Map<TabType, LocateFilter[]>; // current filters for all tabs
  currentFilterUis: Map<TabType, []>; // current filter uis for all tabs
  filterName: string;
  keepSelectedAssetsInFilter: boolean = true; //KHT 08/03/2021 - when true, all selected assets will always appear regardless of filter content
  keepSelectedPeopleInFilter: boolean = true; //KHT 08/16/2021 - when true, all selected people will always appear regardless of filter content

  includeAllPeople: boolean = false;
  includeAllAssets: boolean = false;

  assetObjectProperties: PropertyDescription[];
  peopleObjectProperties: PropertyDescription[];
  locationProperties: PropertyDescription[];

/** Results Per age to be displayed */
  resultsPerPageValues = Locate_ResultsPerPage;
 /** Total # of people (Used to figure out # of pages) */
  totalNumberOfPeople = 0;
   /** Total # of Asssets. (Used to figure out # of pages) */
  totalNumberOfAssets = 0;
  /** Current Page being displayed for the "People" Tab (independent of the Assets Tab's page) */
  currentPeoplePage = 1;
  /** Current Page being displayed for the "Assets" Tab (independent of the People Tab's page) */
  currentAssetPage = 1;

    
  constructor(
    private mLocationService: LocationService,
    private mObjectService: ObjectService,
    private mEnterExitWebSocketService: EntryExitWebSocketService,
    private mImageService: ImageService,
    private cookieService: CookieService,
    private mRoute: ActivatedRoute,
    private spinnerService: NgxSpinnerService
  ) { }

  async ngOnInit(): Promise<void> {

    // Grab all the asset, people and location properties for the type-ahead drop downs in the filters
    this.mRoute.data.subscribe(
      (data: {
        assetPropertiesForMainDisplay: ObjectProperty[],
        peopleObjectParameters: PropertyDescription[],
        locationParameters: PropertyDescription[]
      }) => {
        this.assetObjectProperties = [];

        this.peopleObjectProperties = data.peopleObjectParameters == null ? [] : data.peopleObjectParameters;
        this.addAdditionalObjectProperties(this.peopleObjectProperties);

        this.locationProperties = data.locationParameters == null ? [] : data.locationParameters;
        this.addAdditionalLocationProperties(this.locationProperties);


        /*
         * Set up the Asset Search Options
         */
        {
          const assetSearchOptions: AssetSearchOption[] = [];

          //Create the default "Name" Search Option
          {
            const nameOption = new AssetSearchOption();
            nameOption.mSearchType = SearchType.NAME;
            assetSearchOptions.push(nameOption);
          }

          //Create the default "Group" Search Option
          {
            const groupOption = new AssetSearchOption();
            groupOption.mSearchType = SearchType.GROUP;
            assetSearchOptions.push(groupOption);
          }

          //Creat the Search Options for the Asset Properties marked as being in the "Main Display"
          if (data.assetPropertiesForMainDisplay != null) {
            data.assetPropertiesForMainDisplay.forEach(assetProperty => {
              const propertyOption = new AssetSearchOption();
              propertyOption.mSearchType = SearchType.PROPERTY;
              propertyOption.mProperty = assetProperty;
              assetSearchOptions.push(propertyOption);
            });
          }

          console.log("Created " + assetSearchOptions.length + " Asset Search Options!");
          for (const assetSearchOption of assetSearchOptions) {
            console.log("\tSearch Option: " + assetSearchOption.mSearchType + " - " + (assetSearchOption.mProperty == null ? "*NULL*" : (assetSearchOption.mProperty.property + " (" + assetSearchOption.mProperty.object.name + ")")));
          }

          this.mAssetSearchOptions = assetSearchOptions;
          this.mSelectedAssetSearchOption = this.mAssetSearchOptions[0];
        }
      }

    );

    // Initialize the map object for all the filters in the system
    this.currentFilters = new Map<TabType, LocateFilter[]>();
    this.currentFilterUis = new Map<TabType, []>();

    // Get the cookie if it exists
    console.log("Grab locate cookie");
    this.cookieValue = this.cookieService.get('locate-filters');
    if (this.cookieValue) {
      this.locateFilters = JSON.parse(this.cookieValue);
    } else {
      // if it doesn't exist, set the filters to empty
      this.locateFilters = new LocateFilters();
      this.locateFilters.locationFilters = [];
      this.locateFilters.assetFilters = [];
      this.locateFilters.peopleFilters = [];
    }

    //Create the mPeopleSearchTypes array that gets displayed in the UI selector
    this.mSearchTypes = Object.keys(SearchType).map(key => ({ value: SearchType[key], title: key }));

    //Load the Locations
    try {
      const locationInstances = await this.mLocationService.getAllLocationInstances();

      this.mLocationUis[-1] = [];

      // Grab each location individually, as in the list of all locations, zones are not included
      for (const locationInstance of locationInstances) {
        try {
          const locInstance = await this.mLocationService.getLocationInstanceById(locationInstance.id);
          const ui = new LocationUi();
          ui.mLocation = locInstance;

          // Locations are hierarchical, thus mLocationUis[-1] are all top level locations,
          // and mLocationUis[x] are all child locations under location x
          let mapLocation = -1;
          if (locInstance && locInstance.parent != null) {
            mapLocation = locInstance.parent.instance.id;
          }
          if (this.mLocationUis[mapLocation] == null) {
            this.mLocationUis[mapLocation] = [];
          }

          this.mLocationUis[mapLocation].push(ui);
          this.refreshTab(TabType.LOCATIONS);
        } catch (error) {
          this.handleError(error);
        }
      }
    } catch (error) {
      this.handleError(error);
    }

    //Load the Users
    try {
      const people = await this.mObjectService.getAllPeopleObjectInstancesPaged(0, this.resultsPerPageValues, "ASC", "");
      const uis: PeopleUI[] = [];

      for (const person of people.data) {
        const ui = new PeopleUI();
        ui.person = person;
        ui.parameters = new Map();
        //for(const dataParam of person.data) {
        //  ui.parameters[dataParam.property.property] = dataParam.value;
        //}

        uis.push(ui);
      }

      this.mPeopleUis = uis;
      // Set the total number of people
      this.totalNumberOfPeople = people.size;
      this.refreshTab(TabType.PEOPLE);

    } catch (error) {
      this.handleError(error);
    }

    //Load the Assets
    try {
      console.warn("load assets");
      const assets = await this.mObjectService.getAllAssetObjectInstancesPaged(0, this.resultsPerPageValues, "ASC", "");
      const uis: AssetUI[] = [];
      for (const asset of assets.data) {
        const ui = new AssetUI();
        ui.mAsset = asset;
        ui.mainDisplayParameters = MainDisplayParameter.getDisplayParameters(asset);
        ui.parameters = new Map();
        //for(const dataParam of asset.data) {
        //  ui.parameters[dataParam.property.property] = dataParam.value;
        //}

        uis.push(ui);
      }

      this.mAssetUis = uis;
      this.totalNumberOfAssets = assets.size;
      this.refreshTab(TabType.ASSETS);

    } catch (error) {
      this.handleError(error);
    }
    this.locateMap.turnTrackingOn();
    this.locateMap.useVIP = true;

    // start tracking all objects
    this.startTracking();
  }

  ngOnDestroy(): void {
    // Remove the function from executing on each tick of the createjs.Ticker so that it isn't
    // constantly running in the background and remain attached.
    // createjs.Ticker.off('tick', this.mOnTickFunction);

    //Set the selected everythings to null so that nothing is tracked
    //this.selectUser(null);
    this.selectPeopleGroup(null);
    //this.selectAsset(null);
    this.selectAssetGroup(null);

    this.stopTracking();
  }

  /** The user requested a tab change to either Locations, People or Assets */
  tabChange(tabType: TabType): void {
    if (this.mSelectedTabType != tabType) {
      console.log("Switching Tabs from: " + this.mSelectedTabType + " => " + tabType);

      const oldTab = this.mSelectedTabType;
      this.mSelectedTabType = tabType;

      switch (oldTab) {
        case TabType.LOCATIONS: {
          this.mLocationsSearchValue = '';
          break;
        }
        case TabType.PEOPLE: {
          this.mSelectedPeopleSearchType = SearchType.NAME;
          this.mPeopleSearchValue = '';
          break;
        }
        case TabType.ASSETS: {
          this.mSelectedAssetSearchType = SearchType.NAME;
          this.mAssetSearchValue = '';
          break;
        }
      }

      this.refreshTab(tabType);
    }
  }

  /** is Selected Functions **/
  isLocationSelected(ui: LocationUi): boolean {
    //KHT 09/14/2021 - updated isLocationSelected() to return true for 
    //                  each location in shownLocations, rather than only
    //                  the current one showing - this allows the location
    //                  to remain bold in the LHS after it's selected
    for (var i = 0; i < this.shownLocations.length; i++) {
      if (this.shownLocations[i].locationInstanceId == ui.mLocation.id) {
        return true;
      }
    }
    return false;
    //return (this.mSelectedLocation && this.mSelectedLocation.id == ui.mLocation.id);
  }

  isLocationIdSelected(id: number): boolean {
    return (this.mSelectedLocation && this.mSelectedLocation.id == id);
  }

  isZoneSelected(zone: ZoneInstance): boolean {
    return (this.mSelectedZone &&
      this.mSelectedZone.id == zone.id &&
      this.mSelectedZone.locationInstance.id == zone.locationInstance.id);
  }

  isPeopleGroupSelected(ui: PeopleUI): boolean {
    if (ui.selected) { return true; }
    if (this.mSelectedPeopleGroup == null || ui.person.groups == null) {
      return false;
    } else {
      return ui.person.groups.find(group => group.id == this.mSelectedPeopleGroup.id) != null;
    }
  }

  getIdForLocationLocateHeader(location: LocationInstance): string {
    return `locationsLocateHeader_${location.id}`;
  }

  getIdForLocationLocateBody(location: LocationInstance): string {
    return `locationsLocateBody_${location.id}`;
  }

  locationClicked(ui: LocationUi, hide?: boolean, topParentLocationId?: number): void {
    //if location is already loading, dont register click
    if (this.isLocationLoading) { return; }

    //set selected zone to null
    this.mSelectedZone = null;

    var locationAlreadySelected = false;
    var locationIndex = null;

    for (var i = 0; i < this.shownLocations.length; i++) {
      if (this.shownLocations[i].locationInstanceId == ui.mLocation.id) {
        locationAlreadySelected = true;
        locationIndex = i;
        break;
      }
    }


    //if the clicked location has already been selected
    if (locationAlreadySelected) {
      //remove the location from shownLocations
      this.shownLocations.splice(locationIndex, 1);

      //if there are no other locations selected
      if (this.shownLocations.length == 0) {
        this.isLocationMode = false;
        this.selectLocation(null, true, topParentLocationId);
      }
      //other locations are selected
      else {
        //if the location we're removing is actively being displayed, display the most populous location
        if(this.mSelectedLocation.id == ui.mLocation.id)
          this.showMostPopulousLocation();
        //hide the deselected location's zones in the left hand side
        this._hideLocation(ui.mLocation.id);
      }
      // deselect all assets and people in this location
      // People
      for (const person of this.mPeopleUis) {
        if (
          person.selected &&
          person.person.locationInstance &&
          person.person.locationInstance.id === ui.mLocation.id
        ) {
          this.selectPerson(person, false, true);
        }
      }

      // Assets
      for (const asset of this.mAssetUis) {
        if (
          asset.selected &&
          asset.mAsset.locationInstance &&
          asset.mAsset.locationInstance.id === ui.mLocation.id
        ) {
          this.selectAsset(asset, false, true);
        }
      }

      // reset select-all flags
      this.allPeopleSelected = false;
      this.allAssetsSelected = false;

      // Deselecting also changes scope (either back to the full Select Mode list, or to whichever
      // Location is now shown instead) - reset back to Page 1 so a stale Page number from before isn't
      // reused as the offset into a differently-sized result set.
      this.currentPeoplePage = 1;
      this.currentAssetPage = 1;

      // Refresh the Tab counts
      if (this.shownLocations.length == 0) {
        this.refreshPeopleUi();
        this.refreshAssetUi();
      } else {
        this.refreshTab(TabType.PEOPLE);
        this.refreshTab(TabType.ASSETS);
      }
    }
    //if the clicked location is not already selected
    else {

      this.isLocationMode = true;
      this.mSelectedZone = null;
      var count = 0;

      // Select all assets and people in this location
      for (const people of this.mPeopleUis) {
        if (!people.selected) {
          people.selected = (people.person.locationInstance && people.person.locationInstance.id == ui.mLocation.id);
          if (people.selected) {
            count++;
            this.mCheckedPeople.push(people);
          }
          this.allPeopleSelected = (people.selected) ? true : this.allPeopleSelected;
        }
      }

      for (const asset of this.mAssetUis) {
        if (!asset.selected) {
          asset.selected = (asset.mAsset.locationInstance && asset.mAsset.locationInstance.id == ui.mLocation.id);
          if (asset.selected) {
            count++;
            this.mCheckedAssets.push(asset);
          }
          this.allAssetsSelected = (asset.selected) ? true : this.allAssetsSelected;
        }
      }

      //pass the newly selected location to shownLocations to display above map
      const shownLocation = new ShownLocation();
      shownLocation.locationInstanceId = ui.mLocation.id;
      shownLocation.locationInstanceName = ui.mLocation.name;
      shownLocation.count = count;
      this.shownLocations.push(shownLocation)
      this.selectLocation(ui, false, topParentLocationId)

  // Reset back to Page 1.
      this.currentPeoplePage = 1;
      this.currentAssetPage = 1;

      this.refreshTab(TabType.PEOPLE);
      this.refreshTab(TabType.ASSETS);
    }
  }

  /**
   * Returns how many currently-checked People should count toward the "PEOPLE (X) [Y]" Tab's "[Y]".
   */
  public getCheckedPeopleCountForDisplay(): number {
    if (!this.isLocationMode || this.mSelectedLocation == null) {
      return this.mCheckedPeople.length;
    }
    return this.mCheckedPeople.filter(ui => ui.person.locationInstance != null && ui.person.locationInstance.id == this.mSelectedLocation.id).length;
  }

  /**
   * Returns how many currently-checked Assets should count toward the "ASSETS (X) [Y]" Tab's "[Y]".
   */
  public getCheckedAssetsCountForDisplay(): number {
    if (!this.isLocationMode || this.mSelectedLocation == null) {
      return this.mCheckedAssets.length;
    }
    return this.mCheckedAssets.filter(ui => ui.mAsset.locationInstance != null && ui.mAsset.locationInstance.id == this.mSelectedLocation.id).length;
  }

  getIdForPeopleLocateHeader(ui: PeopleUI): string {
    return `personLocateHeader_${ui.person.id}`;
  }

  getIdForPeopleLocateBody(ui: PeopleUI): string {
    return `personLocateBody_${ui.person.id}`;
  }

  getIdForPeoplePopup(ui: PeopleUI): string {
    return `personPopupDialog_${ui.person.id}`;
  }

  /** select a person on the left hand well */
  async selectPerson(ui: PeopleUI, checked: boolean, keepGroup?: boolean): Promise<void> {
    console.log("selectPerson(" + checked + ")");

    ui.selected = checked;

    if (!keepGroup) {
      this.mSelectedPeopleGroup = null;
    }

    if (checked) {
      // Grab the person's current location and position
      try {
        console.log(`Adding person '${ui.person.name}' to checked people'`);
        this.mCheckedPeople.push(ui);
        console.log("People in mCheckedPeope : " + this.mCheckedPeople.map(p => p.person.name).join(", "));
        ui.person = await this.mObjectService.getObjectInstanceForId(ui.person.id);

        if (this.mSelectedLocation == null && this.mCheckedPeople.length === 0 && this.mCheckedAssets.length === 0) {
          this.locateMap.removeAllPeopleAndAssets();
        }

        // Add this user to the list
        this.locateMap.addPersonToVIP(ui.person);
        this.locateMap.useVIP = true;
        this.locateMap.keepVIPAfterLocationLoad = true;

        // If the user is not in a location, don't show the user
        if (!ui.person.locationInstance) {
          console.log(`Person '${ui.person.name}' is not in a location, so not showing them on the map`);
          return;
        }

        // Change location to this user's location
        if (this.mSelectedLocation == null) {
          this.mSelectedLocation = ui.person.locationInstance;
          this.locateMap.location = this.mSelectedLocation;
        }

        this.addToLocationCount(ui.person);

      } catch (error) {
        this.handleError(error);
      }
    } else {
      if(this.mCheckedPeople.indexOf(ui) != -1){
        console.log(`Removing person '${ui.person.name} from checked people`)
        this.mCheckedPeople.splice(this.mCheckedPeople.indexOf(ui), 1);
      }
      // Set the current selectPerson to false - need to do this for Google Chrome and Firefox browsers
      ui.selected = false;

      this.allPeopleSelected = false;
      var locationIndex = null;
      for (var i = 0; i < this.shownLocations.length; i++) {
        if (this.shownLocations[i].locationInstanceId == ui.person.locationInstance.id) {
          locationIndex = i;
        }
      }

      this.removeLocationIfNecessary(locationIndex);

      // If there is not a VIP active, add all people/assets to the VIP
      if (!this.locateMap.useVIP) {
        // create a VIP list with everyone, except this person
        for (const person of this.mPeopleUis) {
          this.locateMap.addPersonToVIP(person.person);
        }
        for (const asset of this.mAssetUis) {
          this.locateMap.addAssetToVIP(asset.mAsset);
        }

        this.locateMap.useVIP = true;
      }
      this.locateMap.removePersonFromVIP(ui.person.id);
      this.removeFromLocationCount(ui.person, ui.person.locationInstance.id);
    }
  }

  /** select an asset on the left hand well */
  async selectAsset(ui: AssetUI, checked: boolean, keepGroup?: boolean): Promise<void> {
    console.log("selectAsset(" + checked + ")");

    ui.selected = checked;

    if (!keepGroup) {
      this.mSelectedAssetGroup = null;
    }

    if (checked) {
      // Grab this asset's location and position
      try {
        // If the asset is not in a location, don't show the asset
        if (!ui.mAsset.locationInstance) {
          console.log(`Asset '${ui.mAsset.name}' is not in a location, so not showing it on the map`);
          return;
        }

        //KHT 08/03/2021 - add the asset to the 'Checked Assets' object
        console.log(`Adding asset '${ui.mAsset.name}' to checked assets`)
        this.mCheckedAssets.push(ui);

        ui.mAsset = await this.mObjectService.getObjectInstanceForId(ui.mAsset.id);
        // Add this user to the list
        if (this.mSelectedLocation == null) {
          this.locateMap.removeAllPeopleAndAssets();
        }

        this.locateMap.addAssetToVIP(ui.mAsset);
        this.locateMap.useVIP = true;
        this.locateMap.keepVIPAfterLocationLoad = true;

        // If no location is selected, change location to this asset's location
        if (this.mSelectedLocation == null) {
          this.mSelectedLocation = ui.mAsset.locationInstance;
          this.locateMap.location = this.mSelectedLocation;
        }

        this.addToLocationCount(ui.mAsset);

      } catch (error) {
        this.handleError(error);
      }

    } else {
      //KHT 08/03/2021 - make sure the asset is in the 'Checked Assets' object before removing
      if (this.mCheckedAssets.indexOf(ui) != -1) {
        console.log(`Removing asset '${ui.mAsset.name}' from checked assets`);
        this.mCheckedAssets.splice(this.mCheckedAssets.indexOf(ui), 1);
      }
      // Set the current selectAsset to false - need to do this for Google Chrome and Firefox browsers
      ui.selected = false;

      this.allAssetsSelected = false;
      var locationIndex = null;

      for (var i = 0; i < this.shownLocations.length; i++) {
          if (this.shownLocations[i].locationInstanceId == ui.mAsset.locationInstance.id) {
            locationIndex = i;
          }
      }
      //KHT - if the asset was the only object selected in a location, remove the location 
      this.removeLocationIfNecessary(locationIndex);

      // If there is not a VIP active, add all users/assets to the VIP
      if (!this.locateMap.useVIP) {
        // create a VIP list with everyone, except this person
        for (const user of this.mPeopleUis) {
          this.locateMap.addPersonToVIP(user.person);
        }
        for (const asset of this.mAssetUis) {
          this.locateMap.addAssetToVIP(asset.mAsset);
        }

        this.locateMap.useVIP = true;
      }
      this.locateMap.removeAssetFromVIP(ui.mAsset.id);
      this.removeFromLocationCount(ui.mAsset, ui.mAsset.locationInstance.id);

    }
  }

  /** If locations are shown at the top of mapping component in select mode, check to remove a location
   * if there are no people or assets in it anymore.  */
  removeLocationIfNecessary(index?: number): void {
    console.log(`removeLocationIfNecessary(${index})`);
    if (index != null) {
      var locationID = this.shownLocations[index].locationInstanceId;
      let personSelected = false;

      for (const people of this.mPeopleUis) {
        if (people.selected && people.person.locationInstance.id == locationID) {
          personSelected = true;
          break;
        }
      }

      // If no assets are now selected, set location back to null
      let assetSelected = false;
      for (const asset of this.mAssetUis) {
        if (asset.selected && asset.mAsset.locationInstance.id == locationID) {
          assetSelected = true;
          break;
        }
      }
      
      if (!personSelected && !assetSelected) {
        this.shownLocations.splice(index, 1)
        if (this.shownLocations.length > 0) {
          this.showMostPopulousLocation();
        }
        else {
          this.mSelectedLocation = null;
        }

        
      }
    } else {
      // If no users are now selected, set location back to null
      let personSelected = false;

      for (const people of this.mPeopleUis) {
        if (people.selected) {
          personSelected = true;
          break;
        }
      }

      // If no assets are now selected, set location back to null
      let assetSelected = false;
      for (const asset of this.mAssetUis) {
        if (asset.selected) {
          assetSelected = true;
          break;
        }
      }

      if (!personSelected && !assetSelected) {
        this.mSelectedLocation = null;
      }
    }
  }

  personHeaderClicked(ui: PeopleUI): void {

    //Set the selected User Group to null to stop tracking any Users by the selected User Group
    //this.selectPeopleGroup(null);

    if (this.mSelectedPeople[ui.person.id]) {
      //Selecting the same User that was already selected... Unselect it by selecting null
      this.mSelectedPeople[ui.person.id] = null;
      this.hidePersonDetails(ui);
    } else {
      this.mSelectedPeople[ui.person.id] = ui;
      this.showPersonDetails(ui);
    }
  }

  assetHeaderClicked(ui: AssetUI): void {

    //Set the selected Asset Group to null to stop tracking any Assets by the selected Asset Group
    //this.selectAssetGroup(null);

    if (this.mSelectedAssets[ui.mAsset.id]) {
      //Selecting the same Asset that was already selected... Unselect it by selecting null
      this.mSelectedAssets[ui.mAsset.id] = null;
      this.hideAssetDetails(ui);
    } else {
      this.mSelectedAssets[ui.mAsset.id] = ui;
      this.showAssetDetails(ui);
    }
  }

  async selectAllPeople(checked: boolean): Promise<void> {
    // Select all users in the current location
    console.log("selectAllPeople(" + checked + ")");

    for (const user of this.mDisplayedPeopleUis) {
      if (user.selected != checked && user.person.locationInstance) {
        await this.selectPerson(user, checked);
      }
    }

    if (checked) {
      this.showMostPopulousLocation();
    } else {
      this.removeLocationIfNecessary();
    }
  }

  async selectAllAssets(checked: boolean): Promise<void> {
    // Select all assets in the current location
    console.log("selectAllAssets(" + checked + ")");

    for (const asset of this.mDisplayedAssetUis) {
      if (asset.selected != checked && asset.mAsset.locationInstance) {
        await this.selectAsset(asset, checked);
      }
    }

    if (checked) {
      this.showMostPopulousLocation();
    } else {
      this.removeLocationIfNecessary();
    }
  }

  selectLocation(ui: LocationUi, hide?: boolean, topParentLocation?: number): void {
    if (hide == undefined) {
      hide = true;
    }
    if (ui != null) {
      console.log("select Location called with " + ui.mLocation.id + ", hide=" + hide);
    } else {
      console.log("select Location called with NULL ,hide=" + hide);
    }

    if (hide) {
      console.log("hideSelectedLocation");
      this._hideSelectedLocation();
    } else if (ui == null && topParentLocation != null) {
      console.log("hideLocation");
      this._hideLocation(topParentLocation);
    }

    if (ui != null) {
      this.mSelectedLocation = ui.mLocation;
      const shownLocation = new ShownLocation();
      shownLocation.locationInstanceId = this.mSelectedLocation.id;
      shownLocation.locationInstanceName = this.mSelectedLocation.name;
      shownLocation.count = 0;
      this.locateMap.useVIP = true;
      this.locateMap.keepVIPAfterLocationLoad = false;
      this.locateMap.trackPeopleAndAssets = true;
      this.locateMap.location = ui.mLocation;
      this.locateMap.turnTrackingOn();
      this._showSelectedLocation();

    } else {
      this.mSelectedLocation = null;
      this.shownLocations = [];
      this.locateMap.useVIP = false;
      this.locateMap.trackPeopleAndAssets = false;
      this.locateMap.turnTrackingOff();
      this.locateMap.location = null;
    }
  }

  async showPersonDetails(ui: PeopleUI): Promise<void> {
    console.log("showing user details");
    ui.person = await this.mObjectService.getObjectInstanceForId(ui.person.id);
    ui.person.data.sort((a, b) => a.property.order - b.property.order);

    ui.mainDisplayParameters = MainDisplayParameter.getDisplayParameters(ui.person);
    $("#" + this.getIdForPeopleLocateBody(ui)).show();
  }

  hidePersonDetails(ui: PeopleUI): void {
    console.log("hiding user details");
    $("#" + this.getIdForPeopleLocateBody(ui)).hide();
  }

  async showAssetFullDetails(ui: AssetUI): Promise<void> {
    console.log("showing full asset details...");
    // ui.mAsset = await this.mObjectService.getObjectInstanceForId(ui.mAsset.id);
    // ui.mAsset.data.sort((a, b) => a.property.order - b.property.order);

    $("#" + this.getIdForAssetShowLocateDetails(ui)).hide();
    $("#" + this.getIdForAssetHideLocateDetails(ui)).show();
    $("#" + this.getIdForAssetLocateDetails(ui)).show();
  }

  hideAssetFullDetails(ui: AssetUI): void {
    console.log("hiding full asset details...");
    $("#" + this.getIdForAssetHideLocateDetails(ui)).hide();
    $("#" + this.getIdForAssetShowLocateDetails(ui)).show();
    $("#" + this.getIdForAssetLocateDetails(ui)).hide();
  }

  async showAssetDetails(ui: AssetUI): Promise<void> {
    console.log("showing asset details");
    ui.mAsset = await this.mObjectService.getObjectInstanceForId(ui.mAsset.id);
    ui.mAsset.data.sort((a, b) => a.property.order - b.property.order);
    ui.mainDisplayParameters = MainDisplayParameter.getDisplayParameters(ui.mAsset);

    $("#" + this.getIdForAssetLocateBody(ui)).show();
  }

  hideAssetDetails(ui: AssetUI): void {
    console.log("hiding asset details");
    this.hideAssetFullDetails(ui);
    $("#" + this.getIdForAssetLocateBody(ui)).hide();
  }

  showFilterDialog(filterName: string): void {
    this.filterName = filterName;
    this.filterDialog.show();
  }

  hideFilterDialog(): void {
    $("#FILTER_DIALOG").hide();
  }

  startTrackingUser(ui: PeopleUI): void {
    this.mObjectService
      .getObjectInstanceForId(ui.person.id)
      .then(
        person => {
          //Update the User information for the UI Object
          ui.person = person;
          this.locateMap.addPersonToVIP(ui.person);
        },
        error => this.handleError(error)
      );
  }

  getOffsetForPopup(event: Object): Offset {
    const offset = new Offset();
    offset.mLeft = (this.mCanvasPageX) + 'px';
    offset.mTop = (this.mCanvasPageY - 56) + 'px';  //56 is the height of the HeaderComponent
    return offset;
  }

  onWindowResize(): void {

  }

  _hideSelectedLocation(): void {
    if (this.mSelectedLocation != null) {
      $("#" + this.getIdForLocationLocateBody(this.mSelectedLocation)).hide();
    }
  }

  _hideLocation(locationId: number): void {
    $("#locationsLocateBody_" + locationId).hide();
  }

  _showSelectedLocation(): void {
    if (this.mSelectedLocation != null) {
      $("#" + this.getIdForLocationLocateBody(this.mSelectedLocation)).show();
    }
  }

  peopleGroupClicked(ui: PeopleUI, group: Group): void {
    // Cannot click groups when in Location mode
    if (this.isLocationMode) {
      return;
    }

    //Deselect the User (if any is selected)
    //this.selectUser(null);

    //Update the Selected Location to the one the selected User is at
    this.mSelectedLocation = ui.person.locationInstance;

    //Set the selected User Group
    this.selectPeopleGroup(group);
  }

  selectPeopleGroup(group: Group): void {
    //Figure out if the selected User Group is changing
    const changed = (this.mSelectedPeopleGroup == null && group != null)
      || (this.mSelectedPeopleGroup != null && group == null)
      || (this.mSelectedPeopleGroup != null && group != null && this.mSelectedPeopleGroup.id != group.id);

    //If the selected User group is changing, handle the change...
    if (changed) {
      console.log("Selecting User Group: " + (group == null ? "*NULL*" : (group.id + "/" + group.name)));

      //Stop tracking any Users that are currently being tracked due to the Group being selected
      if (this.mPeopleDisplayedForSelectedPeopleGroup != null) {
        this.mPeopleDisplayedForSelectedPeopleGroup.forEach(ui => {
          //Stop tracking the User...
          this.locateMap.removePersonFromVIP(ui.person.id);
        });
        this.mPeopleDisplayedForSelectedPeopleGroup = null;
      }

      // Remove all previous users
      this.mPeopleUis.forEach(ui => {
        this.mSelectedPeople[ui.person.id] = null;
        this.selectPerson(ui, false, true);
      });

      //Set the selected User group to the one passed in
      this.mSelectedPeopleGroup = group;

      if (this.mSelectedPeopleGroup == null && this.mSelectedLocation != null) {
        this.mSelectedLocation = null;
      }

      //Start tracking any Users that are currently part of the selected Group
      this.locateMap.removeAllVIPs();
      this.mPeopleDisplayedForSelectedPeopleGroup = this.mPeopleUis.filter(ui => this.isPeopleGroupSelected(ui));
      this.mPeopleDisplayedForSelectedPeopleGroup.forEach(ui => {
        this.selectPerson(ui, true, true);
        this.startTrackingUser(ui);
      });
    }
  }

  isAssetGroupSelected(ui: AssetUI): boolean {
    if (ui.selected) { return true; }

    if (this.mSelectedAssetGroup == null || ui.mAsset.groups == null) {
      return false;
    } else {
      return ui.mAsset.groups.find(group => group.id == this.mSelectedAssetGroup.id) != null;
    }
  }

  getIdForAssetLocateHeader(ui: AssetUI): string {
    return `assetLocateHeader_${ui.mAsset.id}`;
  }

  getIdForAssetLocateBody(ui: AssetUI): string {
    return `assetLocateBody_${ui.mAsset.id}`;
  }

  getIdForAssetPopup(ui: AssetUI): string {
    return `assetPopupDialog_${ui.mAsset.id}`;
  }

  getIdForAssetLocateDetails(ui: AssetUI): string {
    return `assetLocateDetails_${ui.mAsset.id}`;
  }

  getIdForAssetShowLocateDetails(ui: AssetUI): string {
    return `assetShowLocateDetails_${ui.mAsset.id}`;
  }

  getIdForAssetHideLocateDetails(ui: AssetUI): string {
    return `assetHideLocateDetails_${ui.mAsset.id}`;
  }

  startTrackingAsset(ui: AssetUI): void {
    console.log("Starting to track Asset: " + ui.mAsset.id + "/" + ui.mAsset.name);

    this.mObjectService
      .getObjectInstanceForId(ui.mAsset.id)
      .then(
        asset => {
          //Update the Asset information for the UI Object
          ui.mAsset = asset;

          //Draw the Asset (will only get drawn if it is in the selected Location)
          this.locateMap.addAssetToVIP(asset);
        },
        error => this.handleError(error)
      );
  }

  stopTrackingAsset(ui: AssetUI): void {
    if (ui != null) {
      //Unsubscribe from the Asset's Websocket
      if (ui.mWsSubscription != null) {
        ui.mWsSubscription.unsubscribe();
        ui.mWsSubscription = null;
      }
      if (ui.mWsSubject != null) {
        ui.mWsSubject.unsubscribe();
        ui.mWsSubject = null;
      }

      //Hide the Popup for the Asset if it is currently being displayed...
      ui.mHidePopupEvent.emit(true);

      //Remove the Asset's Icon Image
      this.locateMap.removeAssetFromVIP(ui.mAsset.id);
    }
  }

  assetGroupClicked(ui: AssetUI, group: EmGroup): void {
    // Cannot click groups when in Location mode
    if (this.isLocationMode) {
      return;
    }

    //Deselect any selected Asset
    //this.selectAsset(null);

    //Update the selected Location to the one the Asset whose Group was clicked is at
    this.mSelectedLocation = ui.mAsset.locationInstance;

    //Set the selected Asset Group
    this.selectAssetGroup(group);
  }

  selectAssetGroup(group: EmGroup): void {
    console.log("selectAssetGroup() called");
    //Figure out if the selected Asset Group is changing
    const changed = (this.mSelectedAssetGroup == null && group != null)
      || (this.mSelectedAssetGroup != null && group == null)
      || (this.mSelectedAssetGroup != null && group != null && this.mSelectedAssetGroup.id != group.id);

    //If the selected Asset group is changing, handle the change...
    if (changed) {
      console.log("Selecting Asset Group: " + (group == null ? "*NULL*" : (group.id + "/" + group.name)));

      //Stop tracking any Assets that are currently being tracked due to the Group being selected

      //if (this.mAssetsDisplayedForSelectedAssetGroup != null) {
      //  this.mAssetsDisplayedForSelectedAssetGroup.forEach(ui => this.selectAsset(ui, false)); //this.locateMap.removeAssetFromVIP(ui.mAsset.id));
      //  this.mAssetsDisplayedForSelectedAssetGroup = null;
      //}

      //Set the selected Asset group to the one passed in
      this.mSelectedAssetGroup = group;

      //if (this.mSelectedAssetGroup == null && this.mSelectedLocation != null) {
      //  this.mSelectedLocation = null;
      //}

      // Remove all previous assets
      this.mAssetUis.forEach(ui => {
        //this.mSelectedAssets[ui.mAsset.id] = null;
        this.selectAsset(ui, false, true);
      });

      //Start tracking any Assets that are currently part of the selected Group
      //this.locateMap.removeAllVIPs();

      this.mAssetsDisplayedForSelectedAssetGroup = this.mAssetUis.filter(ui => this.isAssetGroupSelected(ui));
      this.mAssetsDisplayedForSelectedAssetGroup.forEach(ui => {
        this.selectAsset(ui, true, true);
        //this.startTrackingAsset(ui);
      });
    }

  }

  locationsSearchValueChanged(): void {
    console.log("Locations Search Value Changed! Search Value: " + this.mLocationsSearchValue + ", Event: " + event);
    this.selectLocation(null);
    this.refreshTab(TabType.LOCATIONS);
  }

  peopleSearchTypeChanged(): void {
    console.log("People Search Type Changed! => " + this.mSelectedPeopleSearchType + " : " + this.mPeopleSearchValue);

    //Since the Search Type is changing, deselect everything...
    this.selectPeopleGroup(null);

    //Reset the Search Value
    this.mPeopleSearchValue = "";

    // Reset to first page and refresh the displayed people from server
    this.currentPeoplePage = 1;
    this.refreshPeopleUi();
  }

  peopleSearchValueChanged(event: any): void {
    console.log("People Search Value Changed! Search Value: " + this.mPeopleSearchValue + ", Event: " + event);

    //Since the Search Type is changing, deselect everything...
    this.selectPeopleGroup(null);

    // Reset to first page and refresh the displayed people from server
    this.currentPeoplePage = 1;
    this.refreshPeopleUi();
  }

  async refreshTab(tabType: TabType): Promise<void> {
    if (tabType == TabType.ASSETS) {
      await this.refreshAssetTab();
      return;
    }
    if (tabType == TabType.PEOPLE) {
      await this.refreshPeopleTab();
      return;
    }

    // LOCATIONS - the Locations list itself has no concept of being scoped to a "selected Location",
    // so this stays a simple client-side filter over the already-loaded Locations.
    this.refreshDisplayedLocations();
  }

  /**
   * Refreshes "mDisplayedLocationUis" based on the Locations Search Value (and any active
   * advanced Filter) applied over the already-loaded "mLocationUis".
   */
  private refreshDisplayedLocations(): void {
    const searchValue = this.mLocationsSearchValue;
    let filterFrom = this.mLocationUis[-1];

    const uis: LocationUi[] = [];
    if (filterFrom) {
      // if we have a filter we are already filtering on, use it instead of all the UIs
      if (this.currentFilterUis[TabType.LOCATIONS] && this.currentFilterUis[TabType.LOCATIONS].length > 0) {
        filterFrom = this.currentFilterUis[TabType.LOCATIONS];
      }

      filterFrom.forEach(ui => {
        if (searchValue == null || searchValue.length == 0) {
          uis.push(ui);
        } else if (ui.mLocation.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1) {
          uis.push(ui);
        }
      });
    }

    // Sort the locations and assign to the display
    this.mDisplayedLocationUis = uis.sort(function (a: LocationUi, b: LocationUi): number {
      if (a.mLocation.name < b.mLocation.name) {
        return -1;
      } else {
        return 1;
      }
    });
  }

  /**
   * Refreshes "mDisplayedPeopleUis" for the "People" Tab.
   *
   * If we're in Location Mode with a Location selected (and no advanced Filter is active), the People
   * for that Location are fetched directly from the backend instead of being filtered out of the
   * (non-location-scoped) "mPeopleUis" in memory - this ensures paging/totals reflect the People
   * actually in that Location.  Otherwise (Select Mode, "include all", or an advanced Filter is active),
   * falls back to filtering the already-loaded People in memory.
   */
  private async refreshPeopleTab(): Promise<void> {
    const hasAdvancedFilter = this.currentFilterUis[TabType.PEOPLE] && this.currentFilterUis[TabType.PEOPLE].length > 0;

    if (!hasAdvancedFilter && this.isLocationMode && this.mSelectedLocation != null && !this.includeAllPeople) {
      await this.refreshDisplayedPeopleForLocation();
      return;
    }

    this.refreshDisplayedPeopleFromMemory();
  }

  /**
   * Fetches the People in the currently selected Location directly from the backend and updates
   * "mDisplayedPeopleUis" / "totalNumberOfPeople" with the results.
   */
  private async refreshDisplayedPeopleForLocation(): Promise<void> {
    const searchValue = (this.mPeopleSearchValue || '').trim();

    let pagedData: PagedResults<ObjectInstance>;
    try {
      pagedData = await this.mObjectService.getAllObjectInstancesForCategoryInLocationPaged(
        ObjectCategory.PEOPLE,
        this.mSelectedLocation.id,
        (this.currentPeoplePage - 1) * this.resultsPerPageValues,
        this.resultsPerPageValues,
        "ASC",
        searchValue,
        "name"
      );
    } catch (error) {
      this.handleError(error);
      return;
    }

    // Reuse the existing PeopleUI (from "mPeopleUis") for a Person already being tracked, so selection
    // state / object identity stays consistent app-wide; register a new one if this is the first time
    // we've seen them.
    const uis: PeopleUI[] = (pagedData?.data || []).map(person => this.getOrCreatePeopleUi(person));

    // Auto-select everyone fetched for this Location so far - paging further brings in the rest,
    // rather than pretending everyone in the Location is already selected before they've even loaded.
    if (!this.includeAllPeople) {
      uis.forEach(ui => this.autoSelectPersonForLocation(ui));
      // Everyone on this page is now selected, so the "select all" checkbox should read as checked
      this.allPeopleSelected = uis.length > 0;
    }

    this.mDisplayedPeopleUis = uis.sort((a: PeopleUI, b: PeopleUI) => a.person.name < b.person.name ? -1 : 1);
    this.totalNumberOfPeople = pagedData?.size != null ? pagedData.size : this.mDisplayedPeopleUis.length;
  }

  /**
   * Finds the existing PeopleUI for this Person (by ID) in "mPeopleUis" - refreshing its data - so that
   * selection state and object identity stay consistent across fetches; registers a new one if this
   * Person hasn't been loaded before.
   */
  private getOrCreatePeopleUi(person: ObjectInstance): PeopleUI {
    const existing = this.mPeopleUis.find(ui => ui.person.id == person.id);
    if (existing != null) {
      existing.person = person;
      return existing;
    }

    const ui = new PeopleUI();
    ui.person = person;
    ui.parameters = new Map();
    this.mPeopleUis.push(ui);
    return ui;
  }

  /**
   * Marks the given (already Location-scoped) PeopleUI as selected/checked and draws them on the map.
   * The checked-list / Location count bookkeeping only happens once per Person, but the map is always
   * (re-)told to draw them, since some other pre-existing path may have flagged them "selected" already
   * without the map ever finding out.
   *
   * Only People that have actually been paged into (via this method) get drawn on the map - the map
   * never draws everyone in the Location at once, which can overwhelm it with too many items.  Paging
   * further into the Location is what reveals more of them.
   */
  private autoSelectPersonForLocation(ui: PeopleUI): void {
    const alreadySelected = ui.selected;
    ui.selected = true;
    if (!alreadySelected) {
      if (this.mCheckedPeople.indexOf(ui) == -1) {
        this.mCheckedPeople.push(ui);
      }
      this.addToLocationCount(ui.person);
    }
    this.locateMap.addPersonToVIP(ui.person);
  }

  /**
   * Refreshes "mDisplayedPeopleUis" by filtering the already-loaded "mPeopleUis" in memory
   * (Select Mode, "include all People", or an advanced Filter is active).  Also used directly
   * by the live WebSocket movement handlers so real-time updates stay instant / network-free.
   */
  private refreshDisplayedPeopleFromMemory(): void {
    const searchValue = this.mPeopleSearchValue;
    let filterFrom: PeopleUI[] = this.mPeopleUis;

    const uis: PeopleUI[] = [];
    if (filterFrom) {
      // if we have a filter we are already filtering on, use it instead of all the UIs
      if (this.currentFilterUis[TabType.PEOPLE] && this.currentFilterUis[TabType.PEOPLE].length > 0) {
        filterFrom = this.currentFilterUis[TabType.PEOPLE];
      }

      //If we're in location mode, people out of the current location should not be displayed
      if (this.isLocationMode && !this.includeAllPeople && this.mSelectedLocation != null) {
        filterFrom.forEach(ui => {
          if (ui.person.locationInstance != null && ui.person.locationInstance.id == this.mSelectedLocation.id) {
            if (searchValue == null || searchValue.length == 0) {
              uis.push(ui);
            } else if (ui.person.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1) {
              uis.push(ui);
            }
          }
        });
      }
      //Either we're in select mode, or an advanced Filter is active
      else {
        filterFrom.forEach(ui => {
          if (searchValue == null || searchValue.length == 0) {
            uis.push(ui);
          } else if (ui.person.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1) { 
            uis.push(ui);
          }
          //KHT 08/16/2021 -  if a person is selected, not in the filter already, and the 'keepSelectedPeopleInFilter'
          //                  property is true, include the person in the filter
          if (ui.selected && (uis.indexOf(ui) == -1) && this.keepSelectedPeopleInFilter) {
            uis.push(ui)
          }
        });
      }
    }

    // Sort the people and assign to the display
    this.mDisplayedPeopleUis = uis.sort(function (a: PeopleUI, b: PeopleUI): number {
      if (a.person.name < b.person.name) {
        return -1;
      } else {
        return 1;
      }
    });
  }

    private async refreshPeopleUi(): Promise<void> {
      // Show the spinner
      this.spinnerService.show();
  
      // Get the results for the current page / filtering
      let pagedData: PagedResults<ObjectInstance>;
      try {
        // Forward the current people search value to the server so filtering occurs across all pages
        pagedData = await this.mObjectService.getAllPeopleObjectInstancesPaged(
          (this.currentPeoplePage - 1) * this.resultsPerPageValues,
          this.resultsPerPageValues,
          "ASC",
          (this.mPeopleSearchValue || '').trim()
        );
      } catch (error) {
        // Hide the Spinner / Handle the Error
        this.spinnerService.hide();
        this.handleError(error);
        return;
      }
  
      // Update the Page Data and hide the Spinner
       this.updatePagedObjects(TabType.PEOPLE, pagedData);
       this.spinnerService.hide();
    }

    private async refreshAssetUi(): Promise<void> {
      // Show the spinner
      this.spinnerService.show();
  
      // Get the results for the current page / filtering
      let pagedData: PagedResults<ObjectInstance>;
      try {
        // Forward the current asset search value to the server so filtering occurs across all pages
        pagedData = await this.mObjectService.getAllAssetObjectInstancesPaged(
          (this.currentAssetPage - 1) * this.resultsPerPageValues,
          this.resultsPerPageValues,
          "ASC",
          (this.mAssetSearchValue || '').trim()
        );
      } catch (error) {
        // Hide the Spinner / Handle the Error
        this.spinnerService.hide();
        this.handleError(error);
        return;
      }
  
      // Update the Page Data and hide the Spinner
       this.updatePagedObjects(TabType.ASSETS,pagedData);
       this.spinnerService.hide();
    }

    // Update the count of people and assets for each location in the shownLocations array based on the selected people and assets.
    private updateLocationCountForPeopleAndAssets(tabType: TabType): void {
      console.log(`Updating Location Count for Tab: ${tabType}`);
      if(tabType == TabType.PEOPLE) {
            for (const shownLocation of this.shownLocations) {
                console.log(`Resetting count for location ${shownLocation.locationInstanceName} (ID: ${shownLocation.locationInstanceId}) to 0`);
                shownLocation.count = 0;
            }
        }
      else if(tabType == TabType.ASSETS) {
            for (const shownLocation of this.shownLocations) {
                  console.log(`Resetting count for location ${shownLocation.locationInstanceName} (ID: ${shownLocation.locationInstanceId}) to 0`);
                  shownLocation.count = 0;
              }
            }
    this.sortLocationTabs();
  }

    private updatePagedObjects(tabType: TabType, pagedData: PagedResults<ObjectInstance>): void {
      console.log(`Updating Paged Objects for Tab: ${tabType} with ${pagedData.data.length} results`);
    if (!pagedData) return;

    if (tabType === TabType.PEOPLE) {
      const uis: PeopleUI[] = [];
      
      for (const person of (pagedData.data || [])) {
          const ui = new PeopleUI();
          ui.person = person;
          ui.parameters = new Map();
          uis.push(ui);
      }
      
      //Reset all the variables to 0  or empty
      this.resetAfterPageChange(TabType.PEOPLE);

      // Check if its in location mode and if so then Select All the latest fetched people in the current location
      if(this.isLocationMode && this.mSelectedLocation != null) {
        for (const ui of uis) {
          if(ui.person.locationInstance != null && ui.person.locationInstance.id == this.mSelectedLocation.id) {
            this.selectPerson(ui, true);
          }
        }
      }
      // Update the count being displayed for each location after page change
      this.updateLocationCountForPeopleAndAssets(TabType.PEOPLE);

      this.mPeopleUis = uis;
      this.totalNumberOfPeople = pagedData.size != null 
          ? pagedData.size 
          : (this.mPeopleUis ? this.mPeopleUis.length : 0);

      this.refreshTab(TabType.PEOPLE);
    } 
    else if (tabType === TabType.ASSETS) {
      const uis: AssetUI[] = [];

      for (const asset of (pagedData.data || [])) {
          const ui = new AssetUI();
          ui.mAsset = asset;
          ui.parameters = new Map();
          uis.push(ui);
      }

      //Reset all the variables to 0  or empty
      this.resetAfterPageChange(TabType.ASSETS);

      // Reset count of showlocation since people list has changed now
      this.updateLocationCountForPeopleAndAssets(TabType.ASSETS);
      this.mAssetUis = uis;
      this.totalNumberOfAssets = pagedData.size != null 
          ? pagedData.size 
          : (this.mAssetUis ? this.mAssetUis.length : 0);

      this.refreshTab(TabType.ASSETS);
    }
   
}

// Reset After Page Changes function
private resetAfterPageChange(tabType: TabType): void {
    console.log(`Resetting After Page Change for Tab: ${tabType}`);
    switch(tabType) {
      case TabType.PEOPLE:
        {
                this.mCheckedPeople = [];
                this.mSelectedPeople = {};
                this.allPeopleSelected = false;
                console.log("Checked People Reset.  Count: " + this.mCheckedPeople.length);
                this.locateMap.removeAllVIPs();
                break;
        }
      case TabType.ASSETS:
        {
                this.mCheckedAssets = [];
                this.mSelectedAssets = {};
                this.allAssetsSelected = false;
                console.log("Checked Assets Reset.  Count: " + this.mCheckedAssets.length);
                this.locateMap.removeAllVIPs();
                break;
        }
        default:
        {
                console.warn(`Unknown TabType: ${tabType}. No reset performed.`);
                break;
        }

    }
}

  onPageChange(tabType: TabType, pageNumber?: number): void {
    // Validate page number
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    // Each Tab tracks its own page, independent of the other Tab
    const previousPage = (tabType === TabType.PEOPLE) ? this.currentPeoplePage : this.currentAssetPage;

    // Avoid redundant reloads
    if (previousPage === pageNumber) {
      return;
    }

    if (tabType === TabType.PEOPLE) {
      this.currentPeoplePage = pageNumber;
    } else if (tabType === TabType.ASSETS) {
      this.currentAssetPage = pageNumber;
    }

    // If we're viewing a selected Location, page within that Location's own backend-scoped data
    if (this.isLocationMode && this.mSelectedLocation != null) {
      this.refreshTab(tabType);
      return;
    }

    // Otherwise (Select Mode), fetch the requested page of everything from the server, as before
    if (tabType === TabType.PEOPLE) {
      this.refreshPeopleUi();
    } else if (tabType === TabType.ASSETS) {
      this.refreshAssetUi();
    }
  }

  /**
   * Refreshes "mDisplayedAssetUis" for the "Assets" Tab.
   *
   * If we're in Location Mode with a Location selected (no advanced Filter active, and the current
   * search - if any - is something the backend can filter on: none, NAME, or PROPERTY), the Assets
   * for that Location are fetched directly from the backend instead of being filtered out of the
   * (non-location-scoped) "mAssetUis" in memory - this ensures paging/totals reflect the Assets
   * actually in that Location.  Otherwise (Select Mode, "include all", an advanced Filter is active,
   * or a GROUP search - which the backend can't filter on), falls back to filtering the already-loaded
   * Assets in memory.
   */
  private async refreshAssetTab(): Promise<void> {
    const hasAdvancedFilter = this.currentFilterUis[TabType.ASSETS] && this.currentFilterUis[TabType.ASSETS].length > 0;
    const searchOption = this.mSelectedAssetSearchOption;
    const hasSearchValue = this.mAssetSearchValue != null && this.mAssetSearchValue.length > 0;
    const searchIsBackendFilterable = !hasSearchValue || searchOption == null
      || searchOption.mSearchType == SearchType.NAME || searchOption.mSearchType == SearchType.PROPERTY;

    if (!hasAdvancedFilter && this.isLocationMode && this.mSelectedLocation != null && !this.includeAllAssets && searchIsBackendFilterable) {
      await this.refreshDisplayedAssetsForLocation();
      return;
    }

    this.refreshDisplayedAssetsFromMemory();
  }

  /**
   * Fetches the Assets in the currently selected Location directly from the backend and updates
   * "mDisplayedAssetUis" / "totalNumberOfAssets" with the results.
   */
  private async refreshDisplayedAssetsForLocation(): Promise<void> {
    const searchOption = this.mSelectedAssetSearchOption;
    const hasSearchValue = this.mAssetSearchValue != null && this.mAssetSearchValue.length > 0;
    const filterColumn = (hasSearchValue && searchOption?.mSearchType == SearchType.PROPERTY)
      ? searchOption.mProperty?.property
      : "name";

    let pagedData: PagedResults<ObjectInstance>;
    try {
      pagedData = await this.mObjectService.getAllObjectInstancesForCategoryInLocationPaged(
        ObjectCategory.ASSET,
        this.mSelectedLocation.id,
        (this.currentAssetPage - 1) * this.resultsPerPageValues,
        this.resultsPerPageValues,
        "ASC",
        hasSearchValue ? this.mAssetSearchValue.trim() : "",
        filterColumn
      );
    } catch (error) {
      this.handleError(error);
      return;
    }

    // Reuse the existing AssetUI (from "mAssetUis") for an Asset already being tracked, so selection
    // state / object identity stays consistent app-wide; register a new one if this is the first time
    // we've seen it.
    const uis: AssetUI[] = (pagedData?.data || []).map(asset => this.getOrCreateAssetUi(asset));

    // Auto-select every Asset fetched for this Location so far - paging further brings in the rest,
    // rather than pretending everything in the Location is already selected before it's even loaded.
    if (!this.includeAllAssets) {
      uis.forEach(ui => this.autoSelectAssetForLocation(ui));
      // Everything on this page is now selected, so the "select all" checkbox should read as checked
      this.allAssetsSelected = uis.length > 0;
    }

    this.mDisplayedAssetUis = uis.sort((a: AssetUI, b: AssetUI) => a.mAsset.name < b.mAsset.name ? -1 : 1);
    this.totalNumberOfAssets = pagedData?.size != null ? pagedData.size : this.mDisplayedAssetUis.length;
  }

  /**
   * Finds the existing AssetUI for this Asset (by ID) in "mAssetUis" - refreshing its data - so that
   * selection state and object identity stay consistent across fetches; registers a new one if this
   * Asset hasn't been loaded before.
   */
  private getOrCreateAssetUi(asset: ObjectInstance): AssetUI {
    const existing = this.mAssetUis.find(ui => ui.mAsset.id == asset.id);
    if (existing != null) {
      existing.mAsset = asset;
      return existing;
    }

    const ui = new AssetUI();
    ui.mAsset = asset;
    ui.mainDisplayParameters = MainDisplayParameter.getDisplayParameters(asset);
    ui.parameters = new Map();
    this.mAssetUis.push(ui);
    return ui;
  }

  /**
   * Marks the given (already Location-scoped) AssetUI as selected/checked and draws it on the map. The
   * checked-list / Location count bookkeeping only happens once per Asset, but the map is always
   * (re-)told to draw it, since some other pre-existing path may have flagged it "selected" already
   * without the map ever finding out.
   *
   * Only Assets that have actually been paged into (via this method) get drawn on the map - the map
   * never draws everything in the Location at once, which can overwhelm it with too many items.  Paging
   * further into the Location is what reveals more of them.
   */
  private autoSelectAssetForLocation(ui: AssetUI): void {
    // Some other pre-existing selection path (e.g. locationClicked()'s legacy auto-select loop over
    // "mAssetUis") may have already flagged this Asset "selected" without ever telling the map about
    // it. Only skip the checked-list / count bookkeeping when that's already been done - always make
    // sure the map actually knows to draw it.
    const alreadySelected = ui.selected;
    ui.selected = true;
    if (!alreadySelected) {
      if (this.mCheckedAssets.indexOf(ui) == -1) {
        this.mCheckedAssets.push(ui);
      }
      this.addToLocationCount(ui.mAsset);
    }
    this.locateMap.addAssetToVIP(ui.mAsset);
  }

  /**
   * Refreshes "mDisplayedAssetUis" by filtering the already-loaded "mAssetUis" in memory
   * (Select Mode, "include all Assets", a GROUP search, or an advanced Filter is active).  Also used
   * directly by the live WebSocket movement handlers so real-time updates stay instant / network-free.
   */
  private refreshDisplayedAssetsFromMemory(): void {
    console.log("Refreshing Displayed Asset UIs...");
    const uis: AssetUI[] = [];

    if (this.mAssetUis) {
      if (this.mSelectedAssetSearchOption == null || (this.mAssetSearchValue == null || this.mAssetSearchValue.length == 0)) {
        //If we're in location mode, assets out of the current location should not be displayed
        if (this.isLocationMode && !this.includeAllAssets) {
          this.mAssetUis.forEach((assetUI) => {
            if (assetUI.mAsset.locationInstance != null && assetUI.mAsset.locationInstance.id == this.mSelectedLocation.id) {
              uis.push(assetUI)
            }
          })
        } else {
          uis.push(...this.mAssetUis);
        }
      } else {
        console.log("Search Assets using: '" + this.mSelectedAssetSearchOption.mSearchType + "'...");
        switch (this.mSelectedAssetSearchOption.mSearchType) {
          case SearchType.NAME: {
            console.log("Search for Assets with Name like: '" + this.mAssetSearchValue + "'...");
            this.mAssetUis.forEach(ui => {
              if (ui.mAsset.name.toLowerCase().indexOf(this.mAssetSearchValue.toLowerCase()) > -1) {
                //If we're in location mode, assets out of the current location should not be displayed
                if (this.isLocationMode && ui.mAsset.locationInstance.id == this.mSelectedLocation.id && !this.includeAllAssets) {
                  uis.push(ui);
                } else if (!this.isLocationMode) {
                  uis.push(ui);
                }
              }
            });
            break;
          }
          case SearchType.GROUP: {
            this.mAssetUis.forEach(ui => {
              if (ui.mAsset.groups) {
                const foundGroup = ui.mAsset.groups.find(group => {
                  return group.name.toLowerCase().indexOf(this.mAssetSearchValue.toLowerCase()) > -1;
                });
                if (foundGroup) {
                  //If we're in location mode, assets out of the current location should not be displayed
                  if (this.isLocationMode && ui.mAsset.locationInstance.id == this.mSelectedLocation.id && !this.includeAllAssets) {
                    uis.push(ui);
                  } else if (!this.isLocationMode) {
                    uis.push(ui);
                  }
                }
              }
            });
            break;
          }
          case SearchType.PROPERTY: {
            // Get the Property ID of the Property to search for Data for and the Value to be searched
            const propertyId = this.mSelectedAssetSearchOption.mProperty.id;
            const searchValue = this.mAssetSearchValue.trim();
            
            // Cycle through the Asset UIs, adding any that match the Search Criteria to the "uis" list for display
            this.mAssetUis.forEach(ui  => {
              if (ui?.mAsset?.data) {
                // Filter Out Asset Data for only Data that is for the searched for Property
                const foundDataForProperty = ui.mAsset.data.filter(datum => datum?.property?.id == propertyId);
                if (foundDataForProperty.length > 0) {
                  // Find any Asset Data from the match Property Data that fulfills the search criteria
                  const matchingDataForProperty = foundDataForProperty.find(datum => {
                    const dataValue = datum.value;
                    
                    // If there is no Data Value, it don't match
                    if (dataValue == null || dataValue.trim().length == 0) {
                      return false;
                    }

                    // If searching using a "?", then match on any value as long as it isn't null / blank. (Per CMR for Thermofisher)
                    if (searchValue == '?') {
                      return dataValue.trim().length > 0;
                    }

                    // Depending on the Property Type, Match based on the value
                    switch (this.mSelectedAssetSearchOption.mProperty.type) {
                      case PropertyType.INTEGER: {
                        return Number(dataValue) == Number(searchValue);
                      }
                      case PropertyType.STRING:
                      default: {
                        return datum.value.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
                      }
                    }
                  });

                  // If a matching datum was found, the UI should be displayed.  Push it to the "uis" array.
                  if (matchingDataForProperty != null) {
                    uis.push(ui);
                  }
                }
              }
            });
            break;
          }
          default: {
            console.warn("Unknown Asset Search Type: '" + this.mSelectedAssetSearchOption.mSearchType + "'!  Returning all Assets...");
            if (this.isLocationMode) {
              this.mAssetUis.forEach((ui) => {
                if (ui.mAsset.locationInstance.id == this.mSelectedLocation.id && !this.includeAllAssets) {
                  uis.push(ui);
                }
              })
            } else {
              uis.push(...this.mAssetUis);
            }
            break;
          }
        }
        this.mCheckedAssets.forEach(ui => {
          //KHT 08/03/2021 -  if an asset is selected, not in the filter already, and the 'keepSelectedAssetsInFilter' property is true,
          //                  include the asset in the list
          if (ui.selected && (uis.indexOf(ui) == -1) && this.keepSelectedAssetsInFilter) {
            uis.push(ui)
          }
        });
      }
    }

    //Sort the Assets by their Name and set them to the Displayed Assets
    this.mDisplayedAssetUis = uis.sort(function (a: AssetUI, b: AssetUI): number {
      if (a.mAsset.name < b.mAsset.name) {
        return -1;
      } else {
        return 1;
      }
    });
  }

  refreshDisplayedPeople(): void {
    const searchType = this.mSelectedPeopleSearchType;
    const searchValue = this.mPeopleSearchValue;
    const uis = [];
    this.mPeopleUis.forEach(ui => {
      if (searchValue == null || searchValue.length == 0) {
        uis.push(ui);
      } else {
        switch (searchType) {
          case SearchType.NAME: {
            const name = ui.person.name;
            if (name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1) {
              uis.push(ui);
            }
            break;
          }
          case SearchType.GROUP: {
            if (ui.person.groups != null) {
              if (ui.person.groups.find(group => group.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1) != null) {
                uis.push(ui);
              }
            }
            break;
          }
        }
      }
    });
    if (this.mSelectedPeopleSearchType == searchType && this.mPeopleSearchValue == searchValue) {
      this.mDisplayedPeopleUis = uis.sort(function (a: PeopleUI, b: PeopleUI): number {
        if ((a.person.name) < (b.person.name)) {
          return -1;
        } else {
          return 1;
        }
      });

      // Determine if all displayed people are in same location (no location is okay as well)
      let singleLocationId = null;
      this.allPeopleSameLocation = true;
      for (const displayedPeople of this.mDisplayedPeopleUis) {
        const displayedLocationId = (displayedPeople.person.locationInstance) ? displayedPeople.person.locationInstance.id : null;
        if (!singleLocationId && displayedLocationId) {
          singleLocationId = displayedLocationId;
        }
        if (singleLocationId && displayedLocationId && (singleLocationId != displayedLocationId)) {
          this.allPeopleSameLocation = false;
        }
        if (displayedPeople.selected == false) {
          this.allPeopleSelected = false;
        }
      }
    }
  }


  assetSearchTypeChanged(): void {
    console.log("Asset Search Type Changed! => " + this.mSelectedAssetSearchType + " : " + this.mAssetSearchValue);

    this.selectAssetGroup(null);

    ////Reset the Search Value
    this.mAssetSearchValue = "";

    // Reset to first page since the Search is changing
    this.currentAssetPage = 1;

    ////Refresh the Displayed Assets
    this.refreshTab(TabType.ASSETS);
  }

  assetSearchValueChanged(event: any): void {
    console.log("Asset Search Value Changed! Search Value: " + this.mAssetSearchValue + ", Event: " + event);

    this.selectAssetGroup(null);

    // Reset to first page since the Search is changing
    this.currentAssetPage = 1;

    //Refresh the Displayed Asset
    this.refreshTab(TabType.ASSETS);
  }

  zoneHover(zone: ZoneInstance): void {
    // Highlight the current zone when hovered

  }

  zoneClicked(zone: ZoneInstance): void {
    if (this.isLocationIdSelected(zone.locationInstance.id)) {
      // Highlight this zone
      this.mSelectedZone = zone;

      // Move map too zone selected
      this.locateMap.executeZoomToZone(zone);
    }
  }

  // resize event called - unhighlight the selected zone on the locations pane
  mappingEventEmitted(event: MappingEvent): void {
    switch (event) {
      case MappingEvent.LOCATION_LOADED:
        this.isLocationLoading = false;
        break;
      case MappingEvent.ZOOM_OUT:
      case MappingEvent.ZOOM_IN:
      case MappingEvent.ZOOM_TO_ORIGINAL:
        this.mSelectedZone = null;
        break;
      default:
        break;
    }
  }

  handleError(error: any): void {
    console.error("LocateComponent.handleError() - " + error);
    //if (error != null) {
    //  const response: Response = error;
    //  alert(response.text());
    //}
  }

  /** stop tracking all enter/exit events */
  private stopTracking(): void {
    if (this.mEnterExitWsSubject != null) {
      this.mEnterExitWsSubject.unsubscribe();
      this.mEnterExitWsSubject = null;
    }
    if (this.mEnterExitWsSubscription != null) {
      this.mEnterExitWsSubscription.unsubscribe();
      this.mEnterExitWsSubscription = null;
    }
  }

  /** start tracking all enter/exit events to update left-hand tabs. This does not track actual movement - that is in the mapping component */
  private startTracking(): void {
    this.stopTracking();

    this.mEnterExitWsSubject = this.mEnterExitWebSocketService.create();
    this.mEnterExitWsSubscription = this.mEnterExitWsSubject.subscribe(
      message => {
        //Parse the message as an EventInstance
        const event = JSON.parse(message.data) as EventInstance;
        if (event != null && event.event != null) {
          //Currently only handling tracking of People/Assets in here... So only handle events for those Objects
          if (event.event.type == EVENT_TYPE_Asset) {
            console.log("Handling EVENT_TYPE_Asset");
            this.handleAssetEvent(event);
          } else if (event.event.type == EVENT_TYPE_Person) {
            console.log("Handling EVENT_TYPE_Person");
            this.handlePersonEvent(event);
          } else {
            console.log(`Ignoring EnterExit WS Event: ${event.event.type}/${event.event.name}`);
          }
        }
      }
    );

  }

  /** handle asset enter/exit location and zone events for the left-hand tabs */
  private handleAssetEvent(event: EventInstance): void {
    console.log(`handleAssetEvent("${event.event.name}") inside LOCATE`);
    //Attempt to get the Asset the Event is for
    let datum: EventData = null;
    switch (event.event.name) {
      case EVENT_NAME_AssetEnteredLocation:
      case EVENT_NAME_AssetExitedLocation:
      case EVENT_NAME_AssetEnteredZone:
      case EVENT_NAME_AssetExitedZone: {
        datum = event.data.find(d => d.property.property == EVENT_PROP_Asset);
        break;
      }
      default: {
        console.log(`Ignoring Asset Event: ${event.event.name}`);
        return;
      }
    }

    if (datum == null || datum.value == null) {
      console.log(`Ignoring Asset Event: ${event.event.name}, as no Asset data was found for it.`);
      return;
    } else {
      const assetId = Number.parseInt(datum.value, 10);

      for (const assetUi of this.mAssetUis) {
        if (assetUi.mAsset.id == assetId) {
          if (event.event.name == EVENT_NAME_AssetEnteredZone) {
            assetUi.mAsset.zoneInstance = event.zoneInstance;
          }
          if (event.event.name == EVENT_NAME_AssetExitedZone) {
            assetUi.mAsset.zoneInstance = null;
          }

          if (event.event.name == EVENT_NAME_AssetEnteredLocation) {
            const eventLocationId = (event.locationInstance) ? event.locationInstance.id : null;
            const eventPosition = (event.point) ? event.point : null;

            assetUi.mAsset.locationInstance = event.locationInstance;
            console.log("Moving Asset " + assetId + " to " + event.locationInstance.name);
          

            var locationIsSelected = false;
            //KHT - if the location the asset entered is selected, add the asset to it
            for (var i = 0; i < this.shownLocations.length; i++) {
              if (this.shownLocations[i].locationInstanceId == assetUi.mAsset.locationInstance.id) {
                if(this.isLocationMode){
                  assetUi.selected = true;
                }
                this.addToLocationCount(assetUi.mAsset);
                locationIsSelected = true;
                this.locateMap.refreshAssetsInLocation();
              }
            }
            //if the location isn't selected, but the asset is, add the location to the screen
            if(!locationIsSelected && assetUi.selected){
              var newLocation = new ShownLocation();
              newLocation.count = 1;
              newLocation.locationInstanceId = assetUi.mAsset.locationInstance.id;
              newLocation.locationInstanceName = assetUi.mAsset.locationInstance.name;
              this.shownLocations.push(newLocation);
              if(this.shownLocations.length == 1){
                this.mSelectedLocation = assetUi.mAsset.locationInstance;
              }
              this.locateMap.refreshAssetsInLocation();
            }
            
          } else if (event.event.name == EVENT_NAME_AssetExitedLocation) {
            assetUi.mAsset.locationInstance = null;

            if (assetUi.selected && this.isLocationMode) {
              console.log("unselecting asset " + assetUi.mAsset.id);
              assetUi.selected = false;
              this.removeFromLocationCount(assetUi.mAsset, event.locationInstance.id);
              this.locateMap.refreshAssetsInLocation();
            }
            else if (assetUi.selected && !this.isLocationMode){
              this.removeFromLocationCount(assetUi.mAsset, event.locationInstance.id);
              this.locateMap.refreshAssetsInLocation();
            }
            

          }
        }
      }
      // Live movement event - re-filter in memory instead of hitting the backend on every event
      this.refreshDisplayedAssetsFromMemory();
    }
  }

  /** handle person enter/exit location and zone events for the left-hand tabs */
  private handlePersonEvent(event: EventInstance): void {
    console.log(`handlePersonEvent(${event.event.name}) inside LOCATE`)
    //Attempt to get the Person the Event is for
    let datum: EventData = null;
    switch (event.event.name) {
      case EVENT_NAME_PersonEnteredLocation:
      case EVENT_NAME_PersonExitedLocation:
      case EVENT_NAME_PersonEnteredZone:
      case EVENT_NAME_PersonExitedZone: {
        datum = event.data.find(d => d.property.property == EVENT_PROP_Person);
        break;
      }
      default: {
        console.log(`Ignoring Person Event: ${event.event.name}`);
        return;
      }
    }

    if (datum == null || datum.value == null) {
      console.log(`Ignoring Person Event: ${event.event.name}, as no Person data was found for it.`);
      return;
    } else {
      const personId = Number.parseInt(datum.value, 10);

      for (const peopleUi of this.mPeopleUis) {
        if (peopleUi.person.id == personId) {
          if (event.event.name == EVENT_NAME_PersonEnteredZone) {
            peopleUi.person.zoneInstance = event.zoneInstance;
          }
          if (event.event.name == EVENT_NAME_PersonExitedZone) {
            peopleUi.person.zoneInstance = null;
          }
          if (event.event.name == EVENT_NAME_PersonEnteredLocation) {
            const eventLocationId = (event.locationInstance) ? event.locationInstance.id : null;
            const eventPosition = (event.point) ? event.point : null;

            peopleUi.person.locationInstance = event.locationInstance;
            console.log("Moving Person " + personId + " to " + event.locationInstance.name);

            
            var locationIsSelected = false;

            //KHT - if the location the person entered is selected, add the asset to it
            for (var i = 0; i < this.shownLocations.length; i++) {
              if (this.shownLocations[i].locationInstanceId == peopleUi.person.locationInstance.id) {
                if(this.isLocationMode){
                  peopleUi.selected = true;
                }
                this.addToLocationCount(peopleUi.person);
                locationIsSelected = true;
                this.locateMap.refreshPeopleInLocation();
              }
            }

            //if the location isn't selected, but the person is, add the location to the screen
            if(!locationIsSelected && peopleUi.selected){
              var newLocation = new ShownLocation();
              newLocation.count = 1;
              newLocation.locationInstanceId = peopleUi.person.locationInstance.id;
              newLocation.locationInstanceName = peopleUi.person.locationInstance.name;
              this.shownLocations.push(newLocation);
              if(this.shownLocations.length == 1){
                this.mSelectedLocation = peopleUi.person.locationInstance;
              }
              this.locateMap.refreshPeopleInLocation();
            }
          } else if (event.event.name == EVENT_NAME_PersonExitedLocation) {
            peopleUi.person.locationInstance = null;
            console.log("Moving Person " + personId + " to NOT PRESENT ");

            if (peopleUi.selected && this.isLocationMode) {
              console.log("unselecting person " + peopleUi.person.id);
              peopleUi.selected = false;
              this.removeFromLocationCount(peopleUi.person, event.locationInstance.id);
              this.locateMap.refreshPeopleInLocation();
            }else if(peopleUi.selected && !this.isLocationMode){
              this.removeFromLocationCount(peopleUi.person, event.locationInstance.id);
              this.locateMap.refreshPeopleInLocation();
            }
          }
        }
      }
      // Live movement event - re-filter in memory instead of hitting the backend on every event
      this.refreshDisplayedPeopleFromMemory();
    }
  }

  public getImageSourceForObjectInstance(objectInstance: ObjectInstance): string {
    return this.mImageService.getImageSourceForObjectInstance(objectInstance);
  }

  /**
   * Returns a boolean indicating if the Asset Image is present.
   * Used in the HTML to display the Image or not. 
   */
  isAssetImagePresent(asset: ObjectInstance): boolean {
    if (asset.image != null) {
      return true;
    }
    return false;
  }

  public getIconSourceForObjectInstance(objectInstance: ObjectInstance): string {
    return this.mImageService.getIconSourceForObjectInstance(objectInstance);
  }

  public clickLocationButtonSelectMode(locationId: number): void {
    console.log("clickLocationsButtonSelectMode(" + locationId + ")");
    for (const locationUi of this.mDisplayedLocationUis) {
      if (locationUi.mLocation.id == locationId) {
        console.log("clickLocationButtonSelected selectedLocation = " + locationUi.mLocation);
        this.mSelectedLocation = locationUi.mLocation;
      }
    }

    this.currentPeoplePage = 1;
    this.currentAssetPage = 1;

    //KHT - keeps people/asset tab up-to-date with current map selected
    this.refreshTab(this.mSelectedTabType);
  }

  /** Do a full count of location items for items selected */
  public countLocationItemsSelected(): void {
    // Remove all shownLocations
    this.shownLocations.splice(0, this.shownLocations.length);

    for (const assetKey of Object.keys(this.mSelectedAssets)) {
      const assetUi = this.mSelectedAssets[assetKey];

      let found = false;
      for (const shownLocation of this.shownLocations) {
        if (shownLocation.locationInstanceId == assetUi.mAsset.locationInstance.id) {
          shownLocation.count++;
          found = true;
        }
      }

      if (found == false) {
        const newShownLocation = new ShownLocation();
        newShownLocation.locationInstanceId = assetUi.mAsset.locationInstance.id;
        newShownLocation.locationInstanceName = assetUi.mAsset.locationInstance.name;
        newShownLocation.count = 1;
        this.shownLocations.push(newShownLocation);
      }
    }

    this.sortLocationTabs();
  }

  /** add a new location to the list of locations to show above the map */
  public addToLocationCount(objectInstance: ObjectInstance): void {
    console.log(`Add to Location Count - Object ID: ${objectInstance.id} | Location ID: ${objectInstance.locationInstance.id}`);

    // Add this asset to the shownLocation array
    let found = false;
    for (const shownLocation of this.shownLocations) {
      if (shownLocation.locationInstanceId == objectInstance.locationInstance.id) {
        if(objectInstance.object.category == 'ASSET' ){
          for(const asset of this.mAssetUis){
            if(asset.mAsset.id == objectInstance.id && asset.selected){
              shownLocation.count++;
            }
          }
        }
        else if (objectInstance.object.category == 'PEOPLE'){
          for(const person of this.mPeopleUis){
            if(person.person.id == objectInstance.id && person.selected){
              shownLocation.count++;
            }
          }
        }
        found = true;
      }
    }
    if (found == false) {
      const newShownLocation = new ShownLocation();
      newShownLocation.locationInstanceId = objectInstance.locationInstance.id;
      newShownLocation.locationInstanceName = objectInstance.locationInstance.name;
      newShownLocation.count = 1;
      this.shownLocations.push(newShownLocation);
    }

    this.sortLocationTabs();

    if (!this.mSelectedLocation) {
      this.showMostPopulousLocation();
    }

  }

  /** Sort the location tabs above the map in order of most items to least items */
  private sortLocationTabs(): void {
    this.shownLocations = this.shownLocations.sort((a: ShownLocation, b: ShownLocation) => {
      if (a.count > b.count) {
        return -1;
      } else {
        return 1;
      }
    });
  }

  public removeFromLocationCount(objectInstance: ObjectInstance, removeFromLocationId: number): void {
    console.log(`Remove from Location Count - Object: ${objectInstance.id} | Location ID: ${removeFromLocationId}`);
    // Remove this asset from the shownLocation array
    for (var i = 0; i < this.shownLocations.length; i++) {
      if (this.shownLocations[i].locationInstanceId == removeFromLocationId) {
        this.shownLocations[i].count--;
        //if we're in location mode, we don't want the location to be removed from the display even if count is 0
        if (this.shownLocations[i].count == 0 && !this.isLocationMode) {
          const oldLocationID = this.shownLocations[i].locationInstanceId;
          this.shownLocations.splice(i, 1);
          if (this.mSelectedLocation && this.mSelectedLocation.id == oldLocationID) {
            this.showMostPopulousLocation();
          }
        }
      }
    }

    this.sortLocationTabs();
  }

  /** change the display to the location with the most items */
  public showMostPopulousLocation(): void {
    // Set the currently viewed location to the location with the most people/assets
    console.log("showMostPopulousLocation()");
    let maxCount = 0;
    let mostPopulousLocationId = -1;
    for (const shownLocation of this.shownLocations) {
      if (shownLocation.count > maxCount) {
        maxCount = shownLocation.count;
        mostPopulousLocationId = shownLocation.locationInstanceId;
      }
    }
    if (maxCount == 0) {
      this.mSelectedLocation = null;
    } else {
      console.log("showingMostPopulousLocation of " + mostPopulousLocationId + " with count=" + maxCount);
      this.clickLocationButtonSelectMode(mostPopulousLocationId);
    }

  }

  propertiesForSelectedTabType(): PropertyDescription[] {
    switch (this.mSelectedTabType) {
      case TabType.LOCATIONS:
        return this.locationProperties;
      case TabType.ASSETS:
        return this.assetObjectProperties;
      case TabType.PEOPLE:
        return this.peopleObjectProperties;
      default:
        return [];
    }
  }

  /** add additional object items that can be filtered on in the filter popup */
  addAdditionalObjectProperties(objectProperties: PropertyDescription[]): void {
    const locationProperty = new PropertyDescription();
    locationProperty.name = "Location";
    locationProperty.type = PropertyType.LOCATION_REFERENCE;
    objectProperties.push(locationProperty);
    const descriptionProperty = new PropertyDescription();
    descriptionProperty.name = "Description";
    descriptionProperty.type = PropertyType.STRING;
    objectProperties.push(descriptionProperty);
    const tagProperty = new PropertyDescription();
    tagProperty.name = "Tag";
    tagProperty.type = PropertyType.TAG_REFERENCE;
    objectProperties.push(tagProperty);
    const nameProperty = new PropertyDescription();
    nameProperty.name = "Name";
    nameProperty.type = PropertyType.STRING;
    objectProperties.push(nameProperty);
    const typeProperty = new PropertyDescription();
    typeProperty.name = "Type";
    typeProperty.type = PropertyType.OBJECT_REFERENCE;
    objectProperties.push(typeProperty);
  }

  /** add additional location items that can be filtered on in the filter popup */
  addAdditionalLocationProperties(objectProperties: PropertyDescription[]): void {
    const locationTypeProperty = new PropertyDescription();
    locationTypeProperty.name = "Location Type";
    locationTypeProperty.type = PropertyType.LOCATION_REFERENCE;
    objectProperties.push(locationTypeProperty);
    const descriptionProperty = new PropertyDescription();
    descriptionProperty.name = "Description";
    descriptionProperty.type = PropertyType.STRING;
    objectProperties.push(descriptionProperty);
    const nameProperty = new PropertyDescription();
    nameProperty.name = "Name";
    nameProperty.type = PropertyType.STRING;
    objectProperties.push(nameProperty);
  }

  /** Choose a filter. Each tab has it's own independent filters, therefore filterType must be passed along with the filter we need to use */
  filterChosen(filterType: TabType, filter: LocateFilter): void {
    console.log("" + filterType + " Filter Chosen");

    if (!filter) {
      this.currentFilters[filterType] = [];

      // Remove all filters - show all items
      this.currentFilterUis[filterType] = [];
      this.refreshTab(filterType);
      return;
    } else {
      // make sure this isn't a duplicate filter - (does no harm, just looks stupid) - if so, just exit.
      if (this.currentFilters[filterType]) {
        for (const selectedFilter of this.currentFilters[filterType]) {
          if (selectedFilter.name == filter.name) {
            return;
          }
        }
      }
    }

    if (!this.currentFilters[filterType]) {
      this.currentFilters[filterType] = [];
    }
    this.currentFilters[filterType].push(filter);

    let fullMatchingItems = [];

    // Filter locations based on this filter
    //this.mDisplayedLocationUis = [];

    let filterFrom = [];
    let objectType = "";

    switch (filterType) {
      case TabType.LOCATIONS:
        filterFrom = this.mLocationUis[-1];
        objectType = "mLocation";
        break;
      case TabType.PEOPLE:
        filterFrom = this.mPeopleUis;
        objectType = "person";
        break;
      case TabType.ASSETS:
        filterFrom = this.mAssetUis;
        objectType = "mAsset";
        break;
    }

    // if we have a filter we are already filtering on, use it instead of all the UIs
    if (this.currentFilterUis[filterType] && this.currentFilterUis[filterType].length > 0) {
      filterFrom = this.currentFilterUis[filterType];
    }

    let filterNumber = 0;
    for (const param of filter.parameters) {
      let matchingItems = [];

      // Handle filters
      let itemType = "";
      let itemName = "";
      let item2 = "";
      let item3 = "";

      if (param.name == "Description") {
        itemName = "description";
      } else if (param.name == "Name") {
        itemName = "name";
      } else if (param.name == "Type") {
        if (filterType == TabType.ASSETS) {
          itemName = "mAsset";
          item2 = "object";
          item3 = "id";
        } else if (filterType == TabType.PEOPLE) {
          itemName = "person";
          item2 = "object";
          item3 = "id";
        } else {
          itemName = "type";
        }
      } else if (param.name == "Tag") {
        itemName = "tag";
      } else if (param.name == "Location") {
        itemName = "location";
      } else if (param.name.includes(".")) {
        const paramSplit = param.name.split(".");
        itemType = paramSplit[0];
        itemName = paramSplit[1];
      }

      const paramValue = (param.value) ? param.value.toLowerCase() : "";

      if (param.comparison == Comparison.CONTAINS) {
        if (itemType == "") {
          matchingItems = filterFrom.filter(item =>
            (item[objectType] && item[objectType][itemName] && item[objectType][itemName].toLowerCase().includes(paramValue)));
        } else {
          matchingItems = filterFrom.filter(item =>
          (item[objectType].object.name == itemType &&
            item.parameters && item.parameters[itemName] && item.parameters[itemName].toLowerCase().includes(paramValue)));
        }
      } else if (param.comparison == Comparison.IS) {
        if (itemType == "") {
          if (item2 != "" && item3 != "") {
            matchingItems = filterFrom.filter(item =>
              (item[objectType] && item[objectType][item2] && item[objectType][item2][item3] && item[objectType][item2][item3] == param.value));
          } else {
            matchingItems = filterFrom.filter(item =>
              (item[objectType] && item[objectType][itemName] && item[objectType][itemName] == param.value));
          }
        } else {
          matchingItems = filterFrom.filter(item =>
          (item[objectType].object.name == itemType &&
            item.parameters[itemName] && item.parameters[itemName].toLowerCase() == paramValue));
        }
      } else if (param.comparison == Comparison.IS_NOT) {
        if (itemType == "") {
          matchingItems = filterFrom.filter(item =>
            (item[objectType] && item[objectType][itemName] && item[objectType][itemName] != param.value));
        } else {
          matchingItems = filterFrom.filter(item =>
          (item[objectType].object.name == itemType &&
            item.parameters && item.parameters[itemName] && item.parameters[itemName].toLowerCase() != paramValue));
        }
      }

      if (filter.connection == "or") {
        // Handle ORing filters
        // Need to remove all duplicates
        fullMatchingItems = this.squashDuplicates(filterType, fullMatchingItems.concat(matchingItems));
      } else {
        // Handle ANDing filters
        if (filterNumber == 0) {
          fullMatchingItems = fullMatchingItems.concat(matchingItems);
        } else {
          fullMatchingItems = this.onlyDuplicates(filterType, fullMatchingItems.concat(matchingItems));
        }
      }

      filterNumber++;
    }

    // Keep a copy of this filter's results. This will be used to filter on if we have more filters selected
    this.currentFilterUis[filterType] = fullMatchingItems;

    switch (filterType) {
      case TabType.LOCATIONS:
        this.mDisplayedLocationUis = fullMatchingItems;
        break;
      case TabType.PEOPLE:
        this.mDisplayedPeopleUis = fullMatchingItems;
        break;
      case TabType.ASSETS:
        this.mDisplayedAssetUis = fullMatchingItems;
        break;
    }

  }

  removeFilter(filterType: TabType, filter: LocateFilter): void {
    //  currentFilters: Map<TabType, LocateFilter[]>; // current filters for all tabs
    //   currentFilterUis: Map<TabType, []>; // current filter uis for all tabs


    const tempFilters: LocateFilter[] = [];
    for (const aFilter of this.currentFilters[filterType]) {
      if (aFilter.name != filter.name) {
        tempFilters.push(aFilter);
      }
    }

    // Clear the filters
    this.currentFilters[filterType] = [];
    this.currentFilterUis[filterType] = null;

    // Run all the filters
    for (const aFilter of tempFilters) {
      this.filterChosen(filterType, aFilter);
    }

  }

  private squashDuplicates(filterType: TabType, array: any[]): any[] {
    switch (filterType) {
      case TabType.PEOPLE: return this.squashDuplicatePeople(array);
      case TabType.ASSETS: return this.squashDuplicateAssets(array);
      case TabType.LOCATIONS: return this.squashDuplicateLocations(array);
    }
  }

  private onlyDuplicates(filterType: TabType, array: any[]): any[] {
    switch (filterType) {
      case TabType.PEOPLE: return this.onlyDuplicatePeople(array);
      case TabType.ASSETS: return this.onlyDuplicateAssets(array);
      case TabType.LOCATIONS: return this.onlyDuplicateLocations(array);
    }
  }


  /** squash duplicates out of the array by removing duplicate LocationUi elements */
  private squashDuplicateLocations(array: LocationUi[]): LocationUi[] {
    const result = [];
    const elementMap = {};
    for (const element of array) {
      if (!elementMap[element.mLocation.id]) {
        result.push(element);
        elementMap[element.mLocation.id] = true;
      }
    }

    return result;
  }

  /** squash duplicates out of the array by removing duplicate AssetUi elements */
  private squashDuplicateAssets(array: AssetUI[]): AssetUI[] {
    const result = [];
    const elementMap = {};
    for (const element of array) {
      if (!elementMap[element.mAsset.id]) {
        result.push(element);
        elementMap[element.mAsset.id] = true;
      }
    }

    return result;
  }

  /** squash duplicates out of the array by removing duplicate AssetUi elements */
  private squashDuplicatePeople(array: PeopleUI[]): PeopleUI[] {
    const result = [];
    const elementMap = {};
    for (const element of array) {
      if (!elementMap[element.person.id]) {
        result.push(element);
        elementMap[element.person.id] = true;
      }
    }

    return result;
  }

  /** squash duplicates out of the array by removing duplicate LocationUi elements */
  private onlyDuplicateLocations(array: LocationUi[]): LocationUi[] {
    const result = [];
    const elementMap = {};
    for (const element of array) {
      if (!elementMap[element.mLocation.id]) {
        elementMap[element.mLocation.id] = true;
      } else {
        if (!result.find(item => item.mLocation.id == element.mLocation.id)) {
          result.push(element);
        }
      }
    }

    return result;
  }

  private onlyDuplicateAssets(array: AssetUI[]): AssetUI[] {
    const result = [];
    const elementMap = {};
    for (const element of array) {
      if (!elementMap[element.mAsset.id]) {
        elementMap[element.mAsset.id] = true;
      } else {
        if (!result.find(item => item.mAsset.id == element.mAsset.id)) {
          result.push(element);
        }
      }
    }

    return result;
  }

  private onlyDuplicatePeople(array: PeopleUI[]): PeopleUI[] {
    const result = [];
    const elementMap = {};
    for (const element of array) {
      if (!elementMap[element.person.id]) {
        elementMap[element.person.id] = true;
      } else {
        if (!result.find(item => item.person.id == element.person.id)) {
          result.push(element);
        }
      }
    }

    return result;
  }

  /**
   * Returns the Display String for the given measurement
   */
  getDisplayForMeasurement(valueInMeters: number): string {
    return DisplayModule.getDisplayForMeasurement(valueInMeters);
  }

  /**
   * Handles a position changed event on an Asset in the Map
   * @param asset 
   */
  assetPositionChangedEventEmitted(asset: ObjectInstance): void {
    if (asset != null && asset.id != null) {
      const ui = this.mAssetUis.find(ui => ui.mAsset.id == asset.id);
      if (ui != null) {
        console.log("Updating Asset...");
        ui.mAsset.position = asset.position;
        return;
      }
    }
    console.log("NOT Updating Asset");
  }


  getDisplayForAssetSearchOption(assetSearchOption: AssetSearchOption): string {
    switch (assetSearchOption.mSearchType) {
      case SearchType.NAME: {
        return "NAME";
      }
      case SearchType.GROUP: {
        return "GROUP";
      }
      case SearchType.PROPERTY: {
        return assetSearchOption.mProperty.name + " (" + assetSearchOption.mProperty.object.name + ")";
      }
    }
    return null;
  }

  /*
   * Returns a boolean indicating if the "Full Details" section is needed for the given Asset.
   * It is only needed if the Asset has Property Data for a Property that isn't flagged as "Main Display"
   */
  isFullDetailsNeeded(asset: AssetUI): boolean {
    for (let dataField of asset.mAsset.data) {
      if (dataField.property.mainDisplay == false) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns a boolean indicating if the given AssetUI has any Functions that are exposed via
   * it's Asset Type that can be called / executed.  Used to display the Functions as Actions
   * in the Assets Tab when the Asset is expanded.
   */
  isAssetContainsFunctions(asset: AssetUI): boolean {
    if (asset.mAsset.object.functions != null && asset.mAsset.object.functions.length > 0) {
      return true;
    }
    return false;
  }

  /**
   * Called when a Function is clicked on in the Actions dropdown on an Asset.  The corresponding
   * AssetUI and the Function Key for the Function that was clicked are passed as parameters. 
   */
  openAssetFunctionDialog(asset: AssetUI, functionKey: string): void {
    console.log(`Open Asset Function Dialog for Asset: ${asset.mAsset.id}/${asset.mAsset.name}, Function Key: ${functionKey}` );
    this.assetFunctionDialogComponent.setInstanceAndFunction(asset.mAsset, functionKey);
    this.assetFunctionDialogComponent.show();
  }

  /**
   * Handler for the AssetFunctionDialogComponent which gets called on a successful Function
   * execution with an updated version of the Asset the function was performed on.
   * 
   * This replaces the Asset in the UI List with the up-to-date version so that changes
   * to the Property Data can be reflected in the UI.
   */
  onAssetFunctionCompletedSuccessfully(asset: ObjectInstance): void {
    console.log(`Obtained Asset '${asset.id}/${asset.name}' after successfully executing Function!`);
    this.mAssetUis.find(ui => ui.mAsset.id == asset.id).mAsset = asset;
    this.assetFunctionDialogComponent.hide();
  }

  /**
   * Called when the "Edit Data" Action is clicked in the Action(s) dropdown on an Asset. 
   * Displays the AssetDataDialog to allow the User to edit the Data for the given Asset. 
   */
  openAssetDataDialog(asset: AssetUI): void {
    console.log(`Open Asset Data Dialog for Asset: ${asset.mAsset.id}/${asset.mAsset.name}...` );
    this.assetDataDialogComponent.setInstance(asset.mAsset);
    this.assetDataDialogComponent.show();
  }

  /**
   * Handler for the AssetDataDialogComponent which gets called on a successful "Update Data"
   * execution with an updated version of the Asset whose Data was updated.
   * 
   * This replaces the Asset in the UI List with the up-to-date version so that changes
   * to the Property Data can be reflected in the UI.
   */
  onAssetDataUpdatedSuccessfully(asset: ObjectInstance): void {
    console.log(`Obtained Asset '${asset.id}/${asset.name}' after successfully updating Data!`);
    this.mAssetUis.find(ui => ui.mAsset.id == asset.id).mAsset = asset;
    this.assetDataDialogComponent.hide();
  }

  // getAssetDataForMainDisplayValue(asset: ObjectInstance, mainDisplayValue: boolean): InstanceData[] {
  //   // Guard against undefined 'data' and 'property' to avoid runtime/TS strict errors
  //   const dataArray = (asset && asset.data) ? asset.data : [];
  //   return dataArray.filter(d => d && d.property && d.property.mainDisplay === mainDisplayValue);
  // }

  // /*
  // Pagination methods for People & assest tabs
  // */
  //  onPageChange(pageNumber?: number): void {
  //   // Make sure the page number is valid... If not, ignore the call.
  //   if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
  //     return;
  //   }

  //   // Make sure the page number is changing... If not, ignore the call
  //   if (this.currentPage == pageNumber) {
  //     return;
  //   }

  //   // Update the current page number and refresh the data
  //   this.currentPage = pageNumber;
  //   this.refreshTab(this.mSelectedTabType);
  // }

}

enum SearchType {
  NAME = "NAME",
  GROUP = "GROUP",
  PROPERTY = "PROPERTY"
}

class AssetSearchOption {
  mSearchType: SearchType;
  mProperty: ObjectProperty;
}

class LocationUi {
  mLocation: LocationInstance;
}

class PeopleUI {
  person: ObjectInstance;
  mIcon: createjs.Bitmap;
  mLocationId: number;
  mainDisplayParameters: MainDisplayParameter[];
  parameters: Map<string, string>;
  mWsSubject: Subject<MessageEvent>;
  mWsSubscription: Subscription;

  mHidePopupEvent = new EventEmitter<boolean>();

  selected = false;
}

class AssetUI {
  mAsset: ObjectInstance;
  mIcon: createjs.Bitmap;
  mLocationId: number;
  mainDisplayParameters: MainDisplayParameter[];
  parameters: Map<string, string>;
  mWsSubject: Subject<MessageEvent>;
  mWsSubscription: Subscription;

  mHidePopupEvent = new EventEmitter<boolean>();

  selected = false;
}

class ZoneUi {
  mZone: ZoneInstance;
  mShape: createjs.Shape;
}

class Offset {
  mTop: string;
  mLeft: string;
}

/** Holds information about each shown location above the map */
class ShownLocation {
  locationInstanceId: number;
  locationInstanceName: string;
  count: number;
}
